import {settingsTabFor} from '../../features/preferences/settingsTabs';
import { useQuantisVoice } from '../../shared-ui/voice/VoiceContext';
import {requestSettingsNavigation} from './settingsNavigationGuard';
import {publishBrowserHeaderBackAction, clearBrowserHeaderBackAction, type BrowserHeaderBackAction} from './browserHeaderBackAction';
import {SettingsNavigationGroups, type SettingsNavigationGroup} from '../../shared-ui/settings/SettingsNavigationGroups';
import {warmOutreachSections} from '../../features/outreach/outreachReads';
import { AccountingSectionNavigation } from './AccountingSectionNavigation';
import { pipelineMenuItems, outreachMenuItems } from '../../shared-ui/navigation/pipelineMenu';
import { OUTREACH_OPEN_SECTION_EVENT, OUTREACH_SET_SECTION_EVENT } from '../../features/outreach/constants';
import { isContactsHubDestination, isContactsHubView } from './contactsHub';
import { CALENDAR_VIEW_OPTIONS } from './headerActions/calendar';
import { DOCUMENT_FOLDER_NAVIGATION_PORTAL_ID } from '../../features/documents/documentFolderNavigation';
import { useWorkspaceNavigation } from '../../hooks/useWorkspaceNavigation';
import { useWorkspacePlugins } from '../../hooks/useWorkspacePlugins';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import {
  Activity,
  Bell,
  LifeBuoy,
  AlertTriangle,
  ArrowUpRight,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileText,
  FileSignature,
  Settings,
  LogOut,
  LayoutDashboard,
  List,
  RefreshCw,
  Package,
  Receipt,
  Banknote,
  FileCheck,
  Info,
  UserCog,
  ShieldCheck,
  KeyRound,
  ListChecks,
  Shield,
  Users,
  Users2,
  Wrench,
  Loader2,
  type LucideIcon,
} from '@/shared/ui/icons';
import { UserAvatar } from '../../shared/ui/user-avatar';
import { SidebarRightIcon } from '../../shared/ui/SidebarLeftIcon';
import { AdskGlyph } from '../apsViewer/AdskGlyph';
import { visibleSalesSections } from '../../features/sales/salesSections';
import { visiblePurchasesSections } from '../../features/purchases/purchasesSections';
import { CONSULTANCY_STAGES } from '../../features/pipeline/types';
import { usePipelineNavigation, PIPELINE_SELECT_STAGE } from '../../features/pipeline/pipelineNavigation';
import {
  OUTREACH_STATE_EVENT,
  isOutreachSectionId,
  type OutreachSectionId,
} from '../../features/outreach/constants';

import {
  isProjectProcurementTab,
  PROJECT_PROCUREMENT_SECTIONS,
} from '../../features/projects/procurement/navigation';
import {
  APP_UPDATE_STATE_EVENT,
  APP_UPDATE_APPLY_EVENT,
  getLatestAppUpdateState,
  type AppUpdateStateDetail,
} from '../realtime/appUpdateEvents';
import { Popover, PopoverContent, PopoverTrigger } from '../../shared/ui/popover';
import { ACCOUNT_MENU_ROW } from '../presence/PresenceStatusPicker';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../shared/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../shared/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '../../shared/ui/sidebar';
import { cn } from '../../shared/ui/utils';
import { MainRailContextMenu, MainRailEmptyContextMenu, MainRailEmptyMenuIf, type MainRailMenuActions } from './MainRailContextMenu';
import { CustomiseRailDialog } from './CustomiseRailDialog';
import { useMainRailPreferences } from './useMainRailPreferences';
import { canMoveNavigationEntry, moveNavigationEntry, startsNavigationGroup } from '../../shared-ui/navigation/navigationCustomisation';
import { useMainRailLabelMode } from './useMainRailLabelMode';
import { useMainRailShortcuts, type MainRailShortcut } from './useMainRailShortcuts';
import { applyRailOrder, orderByGroup } from './mainRailOrder';
import { MainRailShortcutHint } from './MainRailShortcutHint';
import { useTheme } from '../../app/ThemeProvider';
import { useData } from '../../contexts/DataContext';
import { useNotifications } from '../../features/notifications/NotificationContext';
import { useMessaging } from '../../features/messages/MessagingContext';
import { useNativeBridge } from '../../hooks/useNativeBridge';
import { NativeBridge } from '../../services/NativeBridge';
import {
  getShellPanelMotionTiming,
  isTouchShellRuntime,
  useTabletSidebarLayout,
} from '../../layout-system';
import { useDesktopFrame, useDesktopFrameTouch } from './desktopFrame';
import { useTabletSecondRail } from './useTabletSecondRail';
import {
  buildWorkspacesSidebarTabItems,
  buildWorkspaceDetailSidebarTabItems,
  buildEmployerDetailSidebarTabItems,
  buildEmployerFormSidebarTabItems,
  buildProjectSidebarTabItems,
  buildAccountingSidebarTabItems,
  buildPayrollProfileSidebarTabItems,
  buildPayrollSidebarTabItems,
  buildTaxSidebarTabItems,
  buildSystemSettingsSidebarTabItems,
  buildPreferencesSidebarTabItems,
  buildProfileSidebarTabItems,
  buildEmailSidebarTabItems,
  getSidebarNavGroups,
  isPipelineDestination,
  isAccountingDestination,
  mainNavigationDestination,
  withQuantisAINavigation,
  type SidebarNavItem,
} from './sidebarNavConfig';
import {
  INTERIM_SECTION_ITEMS,
  INTERIM_SECTION_PREFIX,
  HS_EDITOR_SECTION_ITEMS,
  HS_EDITOR_SECTION_PREFIX,
  HS_DOCUMENT_SECTION_ITEMS,
  HS_DOCUMENT_SECTION_PREFIX,
  USER_FORM_SECTION_PREFIX,
  USER_FORM_SECTION_ITEMS,
  EMPLOYER_DETAIL_TAB_PREFIX,
  EMPLOYER_FORM_TAB_PREFIX,
} from '../../app/navigation';
import {
  EMPLOYERS_STATE_EVENT,
  EMPLOYER_FORM_SET_SECTION_EVENT,
  EMPLOYER_FORM_STATE_EVENT,
} from '../../features/employers/constants';
import {
  WORKSPACE_USER_FORM_SET_TAB_EVENT,
  WORKSPACE_USER_FORM_STATE_EVENT,
} from '../../features/users/userRoles';
import { useEmailUnread } from '../../features/email/EmailUnreadContext';
import { Circle, Crosshair, Inbox, Ruler, Trash2, filledIconFor } from '@/shared/ui/icons';
import { useLogos } from '../../hooks/useLogos';
import { useOptionalCall } from '../../features/calls/CallContext';
import { usePresence } from '../../hooks/usePresence';
import { PresenceDot } from '../presence/PresenceDot';
import { PresenceStatusPicker } from '../presence/PresenceStatusPicker';
import { ACCOUNT_MENU_LABELS } from '../../shared-ui/navigation/accountMenu';
import { PRESENCE_LABELS } from '../../types/presence';
import type { NativePresenceDisplayId } from '../../types/native';
import type { PresenceStatus, ResolvedPresenceStatus } from '../../types/presence';
import { SidebarRecentGroup } from './SidebarRecentGroup';
import { openRecentProject, useRecentProjects } from './useRecentProjects';
import { visibleRecent } from '../../shared-ui/navigation/recentItems';
import { useSectionRailCollapsed } from './useSectionRailCollapsed';
import { isProjectReportsTab } from '../../features/reports/project/projectReportCatalog';
import { parseWorkspaceFinancePath, type WorkspaceFinanceHub } from '../../utils/workspaceFinanceRoutes';
import { TakeoffSidebarToolRail } from '../../features/takeoff/tools/TakeoffSidebarToolRail';
import { RAIL_METRICS } from '../../shared-ui/takeoff/TakeoffToolRail';
import {
  VIEW_RAIL_ENTRIES,
  ToolIcon,
  anchorRect,
  type ToolbarContext,
} from '../../features/takeoff/tools/toolbarRegistry';
import { SCALE_PRESETS } from '../../features/takeoff/scalePresets';
import { useDocumentFlag } from './useDocumentFlag';
// Direct URLs - these are public Google Cloud Storage assets, no proxy needed

/** Maps internal PresenceStatus to the native account-menu contract id. */
const PRESENCE_TO_NATIVE: Record<ResolvedPresenceStatus, NativePresenceDisplayId> = {
  available:     'available',
  busy:          'busy',
  dnd:           'dnd',
  be_right_back: 'brb',
  away:          'away',
  offline:       'offline',
  in_meeting:    'in_meeting',
  on_site:       'on_site',
};

function getInitialFinanceSection(hub: WorkspaceFinanceHub, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const route = parseWorkspaceFinancePath(window.location.pathname);
  return route?.hub === hub ? route.section : fallback;
}

/** Mirrors InterimApplication's tabsConfig icons so the sidebar's drilled-in
 *  section rows read identically to the page's own (now-removed) rail. */
const INTERIM_SECTION_ICONS: Record<string, LucideIcon> = {
  summary: LayoutDashboard,
  breakdown: List,
  variations: RefreshCw,
  materials: Package,
  invoice: Receipt,
  payment: Banknote,
  'pay-less': FileCheck,
};

const PROCUREMENT_SECTION_ICONS: Record<string, LucideIcon> = {
  schedule: ClipboardList,
  resources: Wrench,
  enquiries: FileText,
  quotes: List,
  awards: ShieldCheck,
  orders: Package,
};

const BOQ_VIEW_ICONS: Record<string, LucideIcon> = {
  __summary: LayoutDashboard,
  __labour: Users,
  __materials: Package,
  __plant: Wrench,
};

interface BOQNavigationState {
  bills: Array<{ id: string; label: string }>;
  activeBillId?: string | null;
  summaryTabs?: Array<{ id: string; label: string }>;
}

/** Web icons for USER_FORM_SECTION_ITEMS, mirroring the SF Symbols the native
 *  sidebars use for the same rows. */
const USER_FORM_SECTION_ICONS: Record<string, LucideIcon> = {
  overview: UserCog,
  permissions: ShieldCheck,
  security: KeyRound,
  info: Info,
};

const HS_EDITOR_SECTION_ICONS: Record<string, LucideIcon> = {
  'rams-scope': FileText,
  'rams-lift-plan': ArrowUpRight,
  'rams-standards': FileText,
  'rams-drawings': FileText,
  'rams-responsibilities': Users,
  'rams-persons': AlertTriangle,
  'rams-plant': Wrench,
  'rams-coshh': Activity,
  'rams-permits': ClipboardList,
  'rams-method': ClipboardList,
  'rams-hold-points': ListChecks,
  'rams-risks': ListChecks,
  'rams-environmental': Activity,
  'rams-ppe': Shield,
  'rams-emergency': Briefcase,
  'rams-quality-records': ListChecks,
  'rams-handover': Briefcase,
  'rams-references': FileText,
  'rams-distribution': Users,
  'rams-briefing': Users,
};

const HS_DOCUMENT_SECTION_ICONS: Record<string, LucideIcon> = {
  ...HS_EDITOR_SECTION_ICONS,
  'rams-overview': Info,
  'rams-briefing': FileSignature,
};


/** Row treatment for every nav item in this sidebar — the app's own tokens
 *  rather than the shadcn defaults a bare SidebarMenuButton would take. It was
 *  inlined three times (two drill-ins plus the Sales sections) before this;
 *  the third copy went in without the class at all and rendered square-cornered
 *  and untinted next to the rest, which is what extracting it prevents.
 */
const NAV_ITEM_ROW =
  "group relative rounded-[9px] px-2 gap-[12px] h-auto data-[active=true]:bg-nav-item-active-bg data-[active=true]:text-nav-item-active-text text-nav-item-text hover:text-nav-item-text-hover hover:bg-nav-item-hover-bg border-none data-[active=true]:border-none";

/** The same row where the sidebar can collapse to icons — centres the glyph and
 *  drops the horizontal padding at that width. */
const NAV_ITEM_ROW_COLLAPSIBLE =
  `${NAV_ITEM_ROW} group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0`;

interface AppSidebarProps {
  user: any;
  currentView: string;
  onNavigate: (view: string, metadata?: any) => void;
  onShowProfile: () => void;
  onLogout: () => void;
  onShowUserManagement?: () => void;
  onShowWorkspaces?: () => void;
  onShowAdminSupervisorManagement?: () => void;
  isOrgManager?: boolean;
  isProjectDetailView?: boolean;
  projectActiveTab?: string;
  documentPreviewOpen?: boolean;
  onProjectTabChange?: (tab: string) => void;
  projectTabProps?: {
    isClient: boolean;
    isEstimatingPhase: boolean;
    activeTasksCount: number;
    activeClaimsCount: number;
    isStaff: boolean;
    projectId?: string;
    projectName?: string;
    projectStage?: string;
    internalUserIds?: string[];
    runsTender?: boolean;
    issuesContractDocuments?: boolean;
    applicationWorkflow?: import('../../contract-administration/types').ApplicationWorkflow;
    canGivePayLessNotice?: boolean;
  };
  projectPermissions?: any;
  notificationOpen?: boolean;
  onToggleNotifications?: () => void;
  supportOpen?: boolean;
  onToggleSupport?: () => void;
  isSystemSettingsView?: boolean;
  systemSettingsActiveTab?: string;
  onSystemSettingsTabChange?: (tab: string) => void;
  isPreferencesView?: boolean;
  preferencesActiveTab?: string | null;
  onPreferencesTabChange?: (section: string | null) => void;
  isProfileView?: boolean;
  profileActiveTab?: string;
  onProfileTabChange?: (tab: string) => void;
  // Email folder sub-context (Inbox / Sent / Drafts / etc.)
  isEmailView?: boolean;
  emailActiveTab?: string;
  onEmailTabChange?: (tab: string) => void;
  // Client Management sub-context
  isWorkspacesView?: boolean;
  workspacesActiveTab?: 'clients' | 'requests' | 'add-client';
  onWorkspacesTabChange?: (tab: 'clients' | 'requests' | 'add-client') => void;
  workspacesStats?: {
    total: number;
    active: number;
    invited: number;
    pending: number;
    pendingRequests: number;
  };
}

export function AppSidebar({ 
  user, 
  currentView, 
  onNavigate, 
  onShowProfile, 
  onLogout,
  onShowUserManagement,
  onShowWorkspaces,
  onShowAdminSupervisorManagement,
  isOrgManager,
  isProjectDetailView,
  projectActiveTab,
  documentPreviewOpen = false,
  onProjectTabChange,
  projectTabProps,
  projectPermissions,
  notificationOpen,
  onToggleNotifications,
  supportOpen,
  onToggleSupport,
  isSystemSettingsView,
  systemSettingsActiveTab,
  onSystemSettingsTabChange,
  isPreferencesView,
  preferencesActiveTab,
  onPreferencesTabChange,
  isProfileView,
  profileActiveTab,
  onProfileTabChange,
  isEmailView,
  emailActiveTab,
  onEmailTabChange,
  isWorkspacesView,
  workspacesActiveTab,
  onWorkspacesTabChange,
  workspacesStats,
}: AppSidebarProps) {
  const { isMobile, setOpenMobile } = useSidebar();
  const usesTabletSidebarLayout = useTabletSidebarLayout();
  const nativeBridge = useNativeBridge();
  const { theme } = useTheme();
  const { updateUser, getUser, getProject, projects } = useData();
  const dsUser = user?.id ? getUser(user.id) : null;
  const { logoFullLight, logoFullDark, logoIconLight, logoIconDark, isLoading: logosLoading } = useLogos();
  const [profileOpen, setProfileOpen] = useState(false);
  const sidebarElementRef = useRef<HTMLDivElement | null>(null);
  const showProjectTabs = !!isProjectDetailView && !!projectTabProps && !!onProjectTabChange;
  // Documents keeps the project's section rail; its folders are reached through the page's own
  // breadcrumbs, so the sidebar no longer swaps to a folder tree there.
  const documentsNavigationOpen = false;
  const sidebarAnimationRef = useRef<Animation | null>(null);
  const sidebarContentAnimationsRef = useRef<Animation[]>([]);

  const sidebarStartGeometryRef = useRef<{
    width: number;
    marginLeft: number;
    marginRight: number;
    opacity: number;
  } | null>(null);
  // Collapse narrows the desktop sidebar to a 64 px icon rail.
  const [isSidebarCollapsedPref, setIsSidebarCollapsed] = useState(false);
  const [detachedInspectorOpen, setDetachedInspectorOpen] = useState(false);
  const [compactProjectViewport, setCompactProjectViewport] = useState(() => window.innerWidth < 1440);
  const [hsEditorState, setHsEditorState] = useState<{
    activeSection: string;
    sectionIds: readonly string[];
  } | null>(null);
  const [hsDocumentState, setHsDocumentState] = useState<{
    activeSection: string;
    sectionIds: readonly string[];
  } | null>(null);
  const ramsDesktopOutlineOpen =
    !isMobile && !usesTabletSidebarLayout && !!isProjectDetailView &&
    projectActiveTab === 'hs' && (!!hsEditorState || !!hsDocumentState);


  // Collapse means "narrow to an icon rail", which an offcanvas drawer cannot
  // be — but the flag still reached the drawer's contents and silently hid the
  // wordmark and the group headings while the labelled rows stayed, so a phone
  // that had ever hit the (now desktop-only) collapse pill rendered a second,
  // different mobile sidebar. One mobile rendering, always.
  // RAMS is a guided document workspace: keep its section names visible.
  // The stored preference stays untouched and takes effect again on leaving.
  const isSidebarCollapsed = !isMobile && !documentsNavigationOpen && !ramsDesktopOutlineOpen && isSidebarCollapsedPref;
  const captureSidebarGeometry = useCallback((instant = false) => {
    const sidebar = sidebarElementRef.current;
    if (instant) {
      sidebarStartGeometryRef.current = null;
      sidebarAnimationRef.current?.cancel();
      sidebarContentAnimationsRef.current.forEach((animation) => animation.cancel());
    } else if (sidebar && !isMobile) {
      const styles = getComputedStyle(sidebar);
      sidebarStartGeometryRef.current = {
        width: sidebar.getBoundingClientRect().width,
        marginLeft: Number.parseFloat(styles.marginLeft) || 0,
        marginRight: Number.parseFloat(styles.marginRight) || 0,
        opacity: Number.parseFloat(styles.opacity) || 1,
      };
    }
  }, [isMobile]);
  const setDesktopSidebarCollapsed = useCallback((collapsed: boolean, instant = false) => {
    captureSidebarGeometry(instant);
    setIsSidebarCollapsed(collapsed);
  }, [captureSidebarGeometry]);

  useEffect(() => {
    const onInspectorState = (event: Event) => {
      const open = !!(event as CustomEvent<{ open: boolean }>).detail?.open;
      if (open === detachedInspectorOpen) return;
      captureSidebarGeometry();
      setDetachedInspectorOpen(open);
    };
    const onResize = () => {
      const compact = window.innerWidth < 1440;
      if (compact === compactProjectViewport) return;
      captureSidebarGeometry();
      setCompactProjectViewport(compact);
    };
    window.addEventListener('app-sidebar:inspector-state', onInspectorState);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('app-sidebar:inspector-state', onInspectorState);
      window.removeEventListener('resize', onResize);
    };
  }, [captureSidebarGeometry, detachedInspectorOpen, compactProjectViewport]);


  useEffect(() => () => {
    sidebarAnimationRef.current?.cancel();
    sidebarContentAnimationsRef.current.forEach((animation) => animation.cancel());
  }, []);

  // Full-canvas workspaces (Takeoff) can request the same icon-rail state as
  // the visible collapse control without reaching into this component. Keep
  // the command/state pair symmetric so callers can restore only what they
  // changed.
  useLayoutEffect(() => {
    const publish = () => window.dispatchEvent(new CustomEvent('app-sidebar:state-change', {
      detail: { collapsed: isSidebarCollapsed, toggleInHeader: isSidebarCollapsed && usesTabletSidebarLayout },
    }));
    window.addEventListener('app-sidebar:request-state', publish);
    publish();
    return () => window.removeEventListener('app-sidebar:request-state', publish);
  }, [isSidebarCollapsed, usesTabletSidebarLayout]);
  useLayoutEffect(() => {
    const onSetCollapsed = (event: Event) => {
      const detail = (event as CustomEvent<{ collapsed?: boolean; instant?: boolean }>).detail;
      const collapsed = detail?.collapsed;
      if (typeof collapsed === 'boolean') setDesktopSidebarCollapsed(collapsed, detail?.instant);
    };
    const onToggleCollapsed = () => {
      setDesktopSidebarCollapsed(!isSidebarCollapsedPref);
    };
    window.addEventListener('app-sidebar:set-collapsed', onSetCollapsed);
    window.addEventListener('app-sidebar:toggle-collapsed', onToggleCollapsed);
    return () => {
      window.removeEventListener('app-sidebar:set-collapsed', onSetCollapsed);
      window.removeEventListener('app-sidebar:toggle-collapsed', onToggleCollapsed);
    };
  }, [isSidebarCollapsedPref, setDesktopSidebarCollapsed]);
  
  const role: string | undefined =
    user?.role ||
    user?.app_metadata?.role;
  // Recognise both vocabularies — the role-model migration moved
  // admin/staff/client/supervisor → super_admin/internal/member, but
  // some in-flight User objects (cached sessions, demo seed data)
  // may still carry the legacy values. Mirrors the canonical helpers
  // on AuthContext.isAdmin / isStaff / isClient / isSupervisor.
  const isAdmin = role === 'super_admin' || role === 'internal' || role === 'admin' || role === 'staff';
  const isStaff = role === 'internal' || role === 'staff' || isAdmin;
  const isClient =
    role === 'workspace_owner' || role === 'workspace_admin' || role === 'workspace_user' ||
    role === 'member'          || role === 'client';
  const isSupervisor = role === 'workspace_user' || role === 'supervisor';
  // The desktop frame wins over the device: a tablet inside it takes the
  // desktop row metrics and gaps, with touch hit areas added in CSS instead.
  const isDesktopFrame = useDesktopFrame();
  const isTouchRuntime = useMemo(() => {
    if (isDesktopFrame) return false;
    return isTouchShellRuntime({
      userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
      platform: typeof navigator === 'undefined' ? '' : navigator.platform,
      maxTouchPoints: typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
      isDesktopViewport: true,
    });
  }, [isDesktopFrame]);
  const collapsedSidebarTooltip = useCallback((children: React.ReactNode) => (
    isTouchRuntime || usesTabletSidebarLayout
      ? undefined
      // Keep the trigger mounted while the rail animates between widths;
      // swapping it out on collapse drops focus and can flicker on hover.
      : { children, hidden: !isSidebarCollapsed, sideOffset: 8 }
  ), [isSidebarCollapsed, isTouchRuntime, usesTabletSidebarLayout]);
  // For a control with no visible label at any rail width (the brand): the
  // shared tooltip whenever hover exists, rather than a browser title, so the
  // sidebar shows one kind of callout.
  const withHoverTooltip = useCallback((node: React.ReactElement, label: React.ReactNode) => (
    isTouchRuntime || usesTabletSidebarLayout
      ? node
      : (
        <Tooltip>
          <TooltipTrigger asChild>{node}</TooltipTrigger>
          <TooltipContent side="right" align="center" sideOffset={8}>{label}</TooltipContent>
        </Tooltip>
      )
  ), [isTouchRuntime, usesTabletSidebarLayout]);

  const isInSubContext = (!!isProjectDetailView && !!projectTabProps) || !!isSystemSettingsView || !!isPreferencesView || !!isProfileView || !!isWorkspacesView || !!isEmailView;

  const workspaceNavigation = useWorkspaceNavigation(projectTabProps?.projectId);
  const workspaceMainNavigation = useWorkspaceNavigation();
  const projectTabItems = useMemo(
    () => buildProjectSidebarTabItems(projectTabProps, projectPermissions, workspaceNavigation.authoritative ? workspaceNavigation.navigation?.project ?? null : undefined, projectActiveTab),
    [projectPermissions, projectTabProps, workspaceNavigation.authoritative, workspaceNavigation.navigation, projectActiveTab]
  );

  const [boqNavigationState, setBoqNavigationState] = useState<BOQNavigationState | null>(null);
  const sidebarScrollRegionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const applyState = (detail: BOQNavigationState | null | undefined) => {
      setBoqNavigationState(detail ?? null);
    };
    const onStateChange = (event: Event) => {
      applyState((event as CustomEvent<BOQNavigationState | null>).detail);
    };
    window.addEventListener('boq:state-change', onStateChange);
    applyState(
      (window as unknown as { __QUANTIS_BOQ_HEADER_STATE__?: BOQNavigationState | null })
        .__QUANTIS_BOQ_HEADER_STATE__,
    );
    return () => window.removeEventListener('boq:state-change', onStateChange);
  }, []);

  const projectNavigationItems = useMemo(() => {
    if (isProjectProcurementTab(projectActiveTab)) {
      return [
        {
          id: '__h-procurement-module',
          kind: 'heading',
          label: 'Procurement',
          section: 'project',
        },
        ...PROJECT_PROCUREMENT_SECTIONS.map((section) => ({
          id: section.id,
          label: section.label,
          icon: PROCUREMENT_SECTION_ICONS[section.view],
          section: 'project',
        })),
      ];
    }

    // Desktop bills are navigated through the Pricing Summary Inspector.
    // Keep the project destinations available in the second rail.
    if (projectActiveTab === 'pricing' && isMobile && boqNavigationState) {
      return [
        {
          id: '__h-pricing-views',
          kind: 'heading',
          label: 'Pricing',
          section: 'project',
        },
        ...(boqNavigationState.summaryTabs ?? []).map((view) => ({
          id: view.id,
          label: view.label,
          icon: BOQ_VIEW_ICONS[view.id] ?? LayoutDashboard,
          section: 'project',
          navigationKind: 'boq-view',
        })),
        ...(boqNavigationState.bills.length > 0
          ? [
              {
                id: '__h-pricing-bills',
                kind: 'heading',
                label: 'Bills',
                section: 'project',
              },
              ...boqNavigationState.bills.map((bill) => ({
                id: bill.id,
                label: bill.label,
                icon: FileText,
                section: 'project',
                navigationKind: 'boq-view',
              })),
            ]
          : []),
      ];
    }

    return projectTabItems;
  }, [boqNavigationState, projectActiveTab, projectTabItems, isMobile, projectTabProps, projectPermissions, workspaceNavigation.authoritative, workspaceNavigation.navigation]);

  // ── Takeoff tools rail ──────────────────────────────────────────────────
  // With a drawing open, TakeoffView hands this rail over to the drawing tools
  // (takeoff:tools-state-change). One level deeper than the project-section
  // drill-in the rest of the app already does, and the same shape. The
  // registry is imported directly so the toolbar keeps one source of truth;
  // only live state arrives over the event.
  const [takeoffTools, setTakeoffTools] = useState<
    { activeTool: string; ctx: ToolbarContext; viewActive: { view3d: boolean; summary: boolean };
      scale: { denom: number | null } | null; snap: boolean | null;
      ai: { available: boolean; loading: boolean } } | null
  >(null);
  // Lets the back chevron swap to the section list without abandoning the
  // drawing; the top row toggles straight back.
  const [showSectionsOverTools, setShowSectionsOverTools] = useState(false);
  const [takeoffTypesOpen, setTakeoffTypesOpen] = useState(false);
  useEffect(() => {
    const onPanelState = (event: Event) => {
      const detail = (event as CustomEvent<{ section?: string; collapsed?: boolean } | null>).detail;
      setTakeoffTypesOpen(detail?.section === 'types' && !detail.collapsed);
    };
    window.addEventListener('takeoff:panel-state-change', onPanelState);
    return () => window.removeEventListener('takeoff:panel-state-change', onPanelState);
  }, []);
  useEffect(() => {
    const onState = (e: Event) => {
      const d = (e as CustomEvent<{ open?: boolean; activeTool?: string; ctx?: ToolbarContext;
        viewActive?: { view3d: boolean; summary: boolean };
        scale?: { denom: number | null } | null; snap?: boolean | null;
        ai?: { available: boolean; loading: boolean } }>).detail;
      if (!d?.open) { setTakeoffTools(null); setShowSectionsOverTools(false); return; }
      setTakeoffTools({
        activeTool: d.activeTool ?? 'select',
        ctx: (d.ctx ?? {}) as ToolbarContext,
        viewActive: d.viewActive ?? { view3d: false, summary: false },
        scale: d.scale ?? null,
        snap: d.snap ?? null,
        ai: d.ai ?? { available: false, loading: false },
      });
    };
    window.addEventListener('takeoff:tools-state-change', onState);
    return () => window.removeEventListener('takeoff:tools-state-change', onState);
  }, []);
  const takeoffToolsOpen = !!isProjectDetailView && projectActiveTab === 'takeoff' && !!takeoffTools && !showSectionsOverTools;

  const restrictToProjectSections = isMobile && !!isProjectDetailView && projectTabItems.length > 0;
  const enquiriesUnderOutreach = outreachMenuItems(user?.role, workspaceMainNavigation.internalWorkspace === true, (workspaceMainNavigation.navigation?.main ?? []).map(item => item.id)).some(item => item.id === 'enquiries');

  const systemSettingsTabItems = useMemo(() => buildSystemSettingsSidebarTabItems({role:user?.role,includeOutreachTemplates:
    outreachMenuItems(user?.role, workspaceMainNavigation.internalWorkspace === true, (workspaceMainNavigation.navigation?.main ?? []).map(item => item.id)).length > 0,
  }), [user?.role, workspaceMainNavigation.internalWorkspace, workspaceMainNavigation.navigation]);
  // Settings rows nest (`workflows` above `workflows/trigger-catalog`,
  // `templates` above `templates/email`), and a detail page can sit below a row
  // with no row of its own (`integrations/xero`, `templates/email/:id`). So the
  // active row is the LONGEST row id the active section matches — exactly, or
  // as a path prefix. Marking every prefix match active lit up the parent row
  // alongside its own sub-section.
  const activeSystemSettingsTabId = useMemo(() => {
    // Notification templates is a tab of Messages, whose row is templates/email.
    const active = (systemSettingsActiveTab ?? '').replace(/^templates\/notifications(?=\/|$)/, 'templates/email');
    let best: string | null = null;
    for (const { id } of systemSettingsTabItems) {
      if (active !== id && !active.startsWith(`${id}/`)) continue;
      if (!best || id.length > best.length) best = id;
    }
    return best;
  }, [systemSettingsActiveTab, systemSettingsTabItems]);
  const workspacePlugins = useWorkspacePlugins(!!isProfileView);
  // Integrations is the workspace's plugins; the person's own are in Connected accounts.
  const showWorkspacePlugins = workspacePlugins.catalogue?.pageVisible === true && workspacePlugins.catalogue.plugins.some(plugin => plugin.scope !== 'personal' && plugin.capabilities.view);
  const voiceLine = useQuantisVoice()?.config?.enabled === true;
  const profileTabItems = useMemo(
    () => buildProfileSidebarTabItems({ includeWorkspaceSettings: !!isOrgManager, includeAgents: role === 'super_admin', includePlugins: showWorkspacePlugins, includePhone: voiceLine }),
    [isOrgManager, role, showWorkspacePlugins, voiceLine],
  );

  const settingsBackActionRef = useRef<BrowserHeaderBackAction | null>(null);
  const handleSettingsBackAction = useCallback((back: (() => void) | null) => {
    if (settingsBackActionRef.current) clearBrowserHeaderBackAction(settingsBackActionRef.current);
    const action = back ? {id:'settings-categories',label:'Back to Settings',onClick:back} : null;
    settingsBackActionRef.current = action;
    if (action) publishBrowserHeaderBackAction(action);
  }, []);
  useEffect(() => () => handleSettingsBackAction(null), [handleSettingsBackAction]);

  const settingsNavigationGroups = useMemo(() => {
    const groups = new Map<string, SettingsNavigationGroup>();
    for (const [parent, tabs] of [['profile', profileTabItems], ['system-settings', isAdmin ? systemSettingsTabItems : []]] as const) {
      let label = 'You';
      for (const tab of tabs) {
        if (tab.kind === 'heading') { label = tab.label; continue; }
        const groupLabel = label;
        const group = groups.get(groupLabel) ?? {id:groupLabel, label:groupLabel, entries:[]};
        group.entries = [...group.entries, {id:`${parent}:${tab.id}`, label:tab.label, iconPath:tab.icon?.iconPath, available:true}];
        groups.set(groupLabel, group);
      }
    }
    return ['You','Preferences','Workspace','Phone','Automation','Templates','Administration'].flatMap(label => {
      const group = groups.get(label);
      return group ? [group] : [];
    });
  }, [profileTabItems, systemSettingsTabItems, isAdmin]);

  const normalizedProfileTab = profileActiveTab?.startsWith('preferences/') ? `preferences/${settingsTabFor(profileActiveTab.slice('preferences/'.length)) ?? 'general'}` : profileActiveTab;
  const activeProfileSettingsTab = normalizedProfileTab === 'calls' ? 'availability' : normalizedProfileTab === 'account-email' ? 'security' : profileTabItems
    .filter(tab => tab.kind !== 'heading' && (tab.id === normalizedProfileTab || (normalizedProfileTab ?? '').startsWith(`${tab.id}/`)))
    .sort((left, right) => right.id.length - left.id.length)[0]?.id ?? 'overview';

  const settingsNavigationMounted = useRef(true);
  useEffect(() => {settingsNavigationMounted.current = true; return () => {settingsNavigationMounted.current = false;};}, []);
  const settingsGroupsRef = useRef(settingsNavigationGroups);
  settingsGroupsRef.current = settingsNavigationGroups;
  const settingsRouteOwner = useRef<object | null>(null);
  const routeOwner = useMemo(() => ({currentView, profileActiveTab, systemSettingsActiveTab, user, settingsNavigationGroups}), [currentView, profileActiveTab, systemSettingsActiveTab, user, settingsNavigationGroups]);
  settingsRouteOwner.current = routeOwner;
  const openSettingsTarget = async (id: string, closeMenu: boolean) => {
    const activeId = isSystemSettingsView ? `system-settings:${activeSystemSettingsTabId}` : `profile:${activeProfileSettingsTab}`;
    const targetView = id.startsWith('system-settings:') ? 'system-settings' : id.startsWith('profile:workspace/') ? 'profile' : 'account';
    if (!closeMenu && id === activeId && currentView === targetView) return true;
    const owner = settingsRouteOwner.current;
    if (!await requestSettingsNavigation() || !settingsNavigationMounted.current || settingsRouteOwner.current !== owner) return false;
    if (!settingsGroupsRef.current.some(group => group.entries.some(entry => entry.id === id))) return false;
    const separator = id.indexOf(':');
    const parent = id.slice(0, separator);
    const tab = id.slice(separator + 1);
    if (parent === 'system-settings') {
      if (!isSystemSettingsView) onNavigate?.('system-settings');
      onSystemSettingsTabChange?.(tab);
    } else {
      const target = tab.startsWith('workspace/') ? 'profile' : 'account';
      if (currentView !== target) onNavigate?.(target);
      onProfileTabChange?.(tab);
    }
    if (closeMenu) setOpenMobile(false);
    return true;
  };

  const preferencesTabItems = useMemo(() => buildPreferencesSidebarTabItems(), []);
  const showSystemSettingsTabs = !!isSystemSettingsView && !!onSystemSettingsTabChange;
  const showPreferencesTabs = !!isPreferencesView && !!onPreferencesTabChange;
  const showProfileTabs = !!isProfileView && !!onProfileTabChange;

  // Gmail label unread counts come from the app-wide EmailUnreadProvider, which
  // fetches them itself (so the Email badge is live from load) and also absorbs
  // EmailView's broadcast for instant updates while the inbox is open. Drives
  // both the top-level Email badge and the per-folder sub-tab badges.
  const { unreadByLabel: emailUnreadByLabel, inboxUnread: emailInboxUnread } = useEmailUnread();
  // Registered group inboxes — only the ones the current user is a member
  // of (per the email_group_members mirror of Google Groups membership).
  // The user only sees inboxes they're actually entitled to receive on
  // Google's side, matching the existing access-control model. Click →
  // activeLabel becomes 'group:<id>' and the EmailView body short-circuits
  // to the SharedInboxView. Failures are silent (the user just sees the
  // static Gmail folder list and can still navigate).
  const workspaceIdForGroups = (user as any)?.workspace_id ?? (user as any)?.workspaceId ?? null;
  const userIdForGroups = user?.id ?? null;
  const [groupTabs, setGroupTabs] = useState<{ id: string; label: string; address: string }[]>([]);
  useEffect(() => {
    if (!workspaceIdForGroups || !userIdForGroups) { setGroupTabs([]); return; }
    let cancelled = false;
    import('../../services/EmailGroupsService').then(({ emailGroupsService }) => {
      emailGroupsService.list(workspaceIdForGroups, { onlyMine: true, userId: userIdForGroups })
        .then((rows) => {
          if (cancelled) return;
          setGroupTabs(rows.map((r) => ({ id: r.id, label: r.label, address: r.address })));
        })
        .catch(() => { if (!cancelled) setGroupTabs([]); });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [workspaceIdForGroups, userIdForGroups]);

  // Each person's own unread threads per shared inbox. Refreshed when the
  // inbox view reads or unreads a thread, and once a minute for new mail.
  const [groupUnread, setGroupUnread] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!workspaceIdForGroups || groupTabs.length === 0) { setGroupUnread({}); return; }
    let cancelled = false;
    const load = () => {
      import('../../services/SharedInboxService').then(({ sharedInboxService }) =>
        sharedInboxService.unreadCounts(workspaceIdForGroups, groupTabs.map((g) => g.id)))
        .then((counts) => { if (!cancelled) setGroupUnread(counts); })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 60_000);
    window.addEventListener('shared-inbox:state-change', load);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('shared-inbox:state-change', load);
    };
  }, [workspaceIdForGroups, groupTabs]);

  const emailTabItems = useMemo(
    () => {
      const baseTabs = buildEmailSidebarTabItems(emailUnreadByLabel);
      if (groupTabs.length === 0) return baseTabs;
      // Append a heading + one entry per registered group below the
      // standard Gmail folders. id is prefixed `group:` so EmailView's
      // routing in AuthenticatedWorkspace can detect and short-circuit.
      const groupItems: SidebarNavItem[] = [
        { id: '__h-shared-inbox', kind: 'heading', label: 'Shared Inboxes', section: 'main' } as SidebarNavItem,
        ...groupTabs.map((g) => ({
          id: `group:${g.id}`,
          // The section already supplies the shared-inbox context. Omitting
          // the repeated organisation suffix keeps mailbox names readable in
          // the narrow rail without changing their stored names or routes.
          label: g.label.replace(/\s*\|\s*Contract Group$/i, ''),
          icon: Inbox,
          section: 'main',
          sfSymbol: 'tray.2.fill',
          badge: groupUnread[g.id] || undefined,
        }) as SidebarNavItem),
      ];
      return [...baseTabs, ...groupItems];
    },
    [emailUnreadByLabel, groupTabs, groupUnread],
  );

  // The macOS shell owns the visible sidebar, so mirror the same permission-
  // scoped group registry into its native nav payload. The request event makes
  // this resilient to mount order; the shell can ask again after auth or a
  // hard reload without duplicating the groups query.
  useEffect(() => {
    if (!NativeBridge.isRunningNatively()) return;
    const publish = () => window.dispatchEvent(new CustomEvent('email:shared-mailboxes-state', {
      detail: { mailboxes: groupTabs.map(({ id, label }) => ({ id, label })) },
    }));
    window.addEventListener('email:request-shared-mailboxes-state', publish);
    publish();
    return () => window.removeEventListener('email:request-shared-mailboxes-state', publish);
  }, [groupTabs]);

  const showEmailTabs = !!isEmailView && !!onEmailTabChange;

  const workspacesTabItems = useMemo(
    () => buildWorkspacesSidebarTabItems({
      isAdmin,
      pendingRequests: workspacesStats?.pendingRequests,
    }),
    [workspacesStats?.pendingRequests, isAdmin]
  );

  // Workspace detail context — populated by WorkspaceList over a window event
  // bridge whenever a workspace is selected in the directory split. While a
  // workspace is open, the focused-mode sidebar swaps from the directory
  // sub-nav (Directory / Requests / Add Workspace) to the workspace's own
  // section nav (Organisation / Subscription / ...). Kept as local state so
  // the sidebar stays decoupled from WorkspaceList's internal selection.
  const [workspaceDetailContext, setWorkspaceDetailContext] = useState<{
    clientId: string;
    activeTab: string;
  } | null>(null);

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ clientId: string; activeTab: string } | null>).detail;
      setWorkspaceDetailContext(detail ?? null);
    };
    window.addEventListener('workspaces-list:detail-change', onChange as EventListener);
    return () => window.removeEventListener('workspaces-list:detail-change', onChange as EventListener);
  }, []);

  // Drop the detail context the moment we leave the workspaces view (e.g.
  // user clicks Home) so the sidebar doesn't render stale section tabs.
  useEffect(() => {
    if (!isWorkspacesView && workspaceDetailContext) {
      setWorkspaceDetailContext(null);
    }
  }, [isWorkspacesView, workspaceDetailContext]);

  const workspaceDetailTabItems = useMemo(
    () => buildWorkspaceDetailSidebarTabItems(),
    []
  );
  const showWorkspacesTabs = !!isWorkspacesView && !!onWorkspacesTabChange && !!workspaceDetailContext && currentView !== 'add-workspace';

  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{
        activeSection?: string;
        sectionIds?: readonly string[];
      } | null>).detail;
      setHsEditorState(
        detail
          ? {
              activeSection: detail.activeSection ?? 'rams-scope',
              sectionIds: detail.sectionIds ?? [],
            }
          : null,
      );
    };
    window.addEventListener('hs:editor-state-change', onChange as EventListener);
    return () => window.removeEventListener('hs:editor-state-change', onChange as EventListener);
  }, []);
  const hsEditorSectionsOpen =
    !!isProjectDetailView && projectActiveTab === 'hs' && !!hsEditorState;

  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{
        activeSection: string;
        sectionIds: readonly string[];
      } | null>).detail;
      setHsDocumentState(detail ?? null);
    };
    window.addEventListener('hs:document-state-change', onChange);
    return () => window.removeEventListener('hs:document-state-change', onChange);
  }, []);
  const hsDocumentSectionsOpen =
    !!isProjectDetailView && projectActiveTab === 'hs' && !hsEditorSectionsOpen && !!hsDocumentState;
  const hsSectionsOpen = hsEditorSectionsOpen || hsDocumentSectionsOpen;

  // An interim application is open inside the project Applications tab: the
  // sidebar drills in to the application's own section nav (mirrors the
  // native macOS/iPad sidebar) instead of the project tab list, and
  // InterimApplication drops its own in-content SECTIONS rail since this
  // replaces it. Bridge is InterimApplication's 'interim:app-state-change'
  // broadcast (open/close on mount-unmount, activeSection on tab change).
  const [interimAppState, setInterimAppState] = useState<{ open: boolean; activeSection: string } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ open: boolean; activeSection: string }>).detail;
      setInterimAppState(detail?.open ? detail : null);
    };
    window.addEventListener('interim:app-state-change', onChange as EventListener);
    return () => window.removeEventListener('interim:app-state-change', onChange as EventListener);
  }, []);
  const interimSectionsOpen = !!isProjectDetailView && projectActiveTab === 'interim' && !!interimAppState?.open;

  // The edit-user form is open inside a workspace's Users tab: drill one level
  // past the workspace section list to the form's own sections, exactly as the
  // interim block above does for an open application. EditUserForm drops its
  // in-content TabsList in return.
  const [userFormState, setUserFormState] = useState<{ activeTab?: string } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      setUserFormState((e as CustomEvent<{ activeTab?: string } | null>).detail ?? null);
    };
    window.addEventListener(WORKSPACE_USER_FORM_STATE_EVENT, onChange as EventListener);
    return () => window.removeEventListener(WORKSPACE_USER_FORM_STATE_EVENT, onChange as EventListener);
  }, []);
  // Same guard as the workspace detail context: drop it the moment we leave
  // the workspaces view so stale section rows can't linger.
  const inWorkspaceUserContext = !!isWorkspacesView || (!!isProfileView && (profileActiveTab ?? '').startsWith('workspace/users'));
  useEffect(() => {
    if (!inWorkspaceUserContext && userFormState) setUserFormState(null);
  }, [inWorkspaceUserContext, userFormState]);
  const userFormSectionsOpen = inWorkspaceUserContext && !!userFormState;

  // An employer profile is open: the sidebar swaps the app nav for the
  // profile's own sections, the same drill-in the Workspaces directory makes
  // one level down. EmployerManagement's state broadcast carries the selected
  // employer's name and is null while the directory or the add form is up.
  const [employerDetailName, setEmployerDetailName] = useState<string | null>(null);
  // Read the section off the same broadcast rather than tracking clicks here.
  // A row click is not the only way the page changes: a deeplink to
  // /employers/[slug]/[section] picks one before this sidebar has seen a click,
  // and tracking clicks alone left the highlight on Details over a Contacts page.
  const [employerActiveSection, setEmployerActiveSection] = useState<string>('details');
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ selectedName?: string | null; activeSection?: string | null } | null>).detail;
      setEmployerDetailName(detail?.selectedName ?? null);
      setEmployerActiveSection(detail?.activeSection ?? 'details');
    };
    window.addEventListener(EMPLOYERS_STATE_EVENT, onChange as EventListener);
    return () => window.removeEventListener(EMPLOYERS_STATE_EVENT, onChange as EventListener);
  }, []);
  const isEmployersView = currentView === 'employers';
  // Same guard as the two blocks above: drop it the moment we leave the view,
  // or clicking Home leaves the employer's section rows rendering over the
  // dashboard.
  useEffect(() => {
    if (!isEmployersView && employerDetailName) setEmployerDetailName(null);
  }, [isEmployersView, employerDetailName]);
  const employerSectionsOpen = isEmployersView && !!employerDetailName;

  // Sales drill-in. Unlike the employer/workspace ones there is no record to
  // open — being on the view IS the drill-in — so this keys off currentView
  // rather than a broadcast name. The ACTIVE section still comes from the
  // hub's broadcast, for the same reason the employer one does: a deeplink or
  // a native nav can change it without a click landing here.
  const messagesSectionsOpen = currentView === 'messages' && !isMobile && !usesTabletSidebarLayout && !NativeBridge.isRunningNatively();
  const salesSectionsOpen = currentView === 'sales';
  const pipelineNavigation = usePipelineNavigation();
  const calendarSectionsOpen = currentView === 'calendar' && isMobile && !NativeBridge.isRunningNatively();
  const [calendarActiveView, setCalendarActiveView] = useState('agenda');
  useEffect(() => {
    const onChange = (event: Event) => {
      const view = (event as CustomEvent<{ view?: string } | null>).detail?.view;
      if (view) setCalendarActiveView(view);
    };
    window.addEventListener('calendar:state-change', onChange);
    return () => window.removeEventListener('calendar:state-change', onChange);
  }, []);

  const [salesActiveSection, setSalesActiveSection] = useState<string>('overview');
  useEffect(() => {
    const onChange = (e: Event) => {
      const id = (e as CustomEvent<{ sectionId?: string }>).detail?.sectionId;
      if (id) setSalesActiveSection(id);
    };
    window.addEventListener('sales:section-change', onChange);
    return () => window.removeEventListener('sales:section-change', onChange);
  }, []);
  // `role` above already normalises the two vocabularies and the metadata
  // fallbacks, so reuse it rather than reading user.role again here.
  const salesSections = useMemo(() => visibleSalesSections(role), [role]);

  // Purchases drill-in — same shape as Sales above, for the same reason: being
  // on the view IS the drill-in, so this keys off currentView rather than a
  // broadcast name, and the active section still comes from the hub's own
  // broadcast since a deeplink or native nav can change it without a click
  // landing here.
  const purchasesSectionsOpen = currentView === 'purchases';
  const [purchasesActiveSection, setPurchasesActiveSection] = useState<string>('overview');
  useEffect(() => {
    const onChange = (e: Event) => {
      const id = (e as CustomEvent<{ sectionId?: string }>).detail?.sectionId;
      if (id) setPurchasesActiveSection(id);
    };
    window.addEventListener('purchases:section-change', onChange);
    return () => window.removeEventListener('purchases:section-change', onChange);
  }, []);
  const purchasesSections = useMemo(() => visiblePurchasesSections(role), [role]);

  // Outreach drill-in — same shape as Sales/Purchases above: being on the
  // view IS the drill-in, so this keys off currentView, and the active
  // section comes from OUTREACH_STATE_EVENT (payload `{ activeSection }`, or
  // null on unmount), which OutreachView already publishes for exactly this.
  // Clicking a row routes back through OUTREACH_SET_SECTION_EVENT, which
  // OutreachView listens for — the sidebar never owns the active section, it
  // only shows it.
  const [outreachActiveSection, setOutreachActiveSection] = useState<OutreachSectionId>('approvals');
  useEffect(() => {
    const onChange = (e: Event) => {
      const activeSection = (e as CustomEvent<{ activeSection?: string } | null>).detail?.activeSection;
      if (isOutreachSectionId(activeSection)) setOutreachActiveSection(activeSection);
    };
    window.addEventListener(OUTREACH_STATE_EVENT, onChange);
    return () => window.removeEventListener(OUTREACH_STATE_EVENT, onChange);
  }, []);

  // Payroll drill-in. The page broadcasts its active section and whether an
  // employee profile is open; this keeps the global sidebar as the single
  // desktop navigation surface while leaving PayrollView in charge of data.
  const payrollSectionsOpen = currentView === 'payroll';
  const [payrollActiveSection, setPayrollActiveSection] = useState<string>(() =>
    getInitialFinanceSection('payroll', 'overview')
  );
  const [payrollProfileState, setPayrollProfileState] = useState<{
    open: boolean;
    activeTab: string;
  }>({ open: false, activeTab: 'overview' });
  useEffect(() => {
    const onSectionChange = (event: Event) => {
      const id = (event as CustomEvent<{ sectionId?: string }>).detail?.sectionId;
      if (id) setPayrollActiveSection(id);
    };
    const onProfileState = (event: Event) => {
      const detail = (event as CustomEvent<{
        hub?: string;
        profileOpen?: boolean;
        profileTab?: string;
      } | null>).detail;
      if (detail?.hub !== 'payroll' || !detail.profileOpen) {
        setPayrollProfileState({ open: false, activeTab: 'overview' });
        return;
      }
      setPayrollProfileState({
        open: true,
        activeTab: detail.profileTab || 'overview',
      });
    };
    window.addEventListener('payroll:section-change', onSectionChange);
    window.addEventListener('payroll-tax:state-change', onProfileState);
    return () => {
      window.removeEventListener('payroll:section-change', onSectionChange);
      window.removeEventListener('payroll-tax:state-change', onProfileState);
    };
  }, []);
  const payrollSectionItems = useMemo(() => buildPayrollSidebarTabItems(), []);
  const payrollProfileItems = useMemo(() => buildPayrollProfileSidebarTabItems(), []);

  const accountingSectionsOpen = currentView === 'accounting';
  const [accountingActiveSection, setAccountingActiveSection] = useState(() =>
    getInitialFinanceSection('accounting', 'bank-accounts')
  );
  useEffect(() => {
    const onChange = (event: Event) => {
      const id = (event as CustomEvent<{ sectionId?: string }>).detail?.sectionId;
      if (id) setAccountingActiveSection(id);
    };
    window.addEventListener('accounting:section-change', onChange);
    return () => window.removeEventListener('accounting:section-change', onChange);
  }, []);
  const accountingSectionItems = useMemo(() => buildAccountingSidebarTabItems(), []);

  const taxSectionsOpen = currentView === 'tax';
  const [taxActiveSection, setTaxActiveSection] = useState(() =>
    getInitialFinanceSection('tax', 'vat-returns')
  );
  useEffect(() => {
    const onChange = (event: Event) => {
      const id = (event as CustomEvent<{ sectionId?: string }>).detail?.sectionId;
      if (id) setTaxActiveSection(id);
    };
    window.addEventListener('tax:section-change', onChange);
    return () => window.removeEventListener('tax:section-change', onChange);
  }, []);
  const taxSectionItems = useMemo(() => buildTaxSidebarTabItems(), []);
  const financeHubSectionsOpen = payrollSectionsOpen || accountingSectionsOpen || taxSectionsOpen;
  const financeHubParent = payrollSectionsOpen
    ? 'payroll'
    : accountingSectionsOpen
      ? 'accounting'
      : 'tax';
  const financeHubItems = payrollSectionsOpen
    ? (payrollProfileState.open ? payrollProfileItems : payrollSectionItems)
    : accountingSectionsOpen
      ? accountingSectionItems
      : taxSectionItems;

  const employerDetailTabItems = useMemo(() => buildEmployerDetailSidebarTabItems(), []);

  // The employer add/edit form is open: drill one level past the profile above
  // to the form's own sections, exactly as the edit-user block does inside a
  // workspace, and EmployerForm drops its in-content nav column in return.
  // `sectionIds` rides the broadcast because the form's list is conditional
  // (Credit Check appears only once there is a company to check) — a fixed set
  // of rows here would offer a page the form refuses to render.
  const [employerFormState, setEmployerFormState] = useState<{
    activeSection: string;
    sectionIds: readonly string[];
  } | null>(null);
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ activeSection?: string; sectionIds?: readonly string[] } | null>).detail;
      setEmployerFormState(
        detail
          ? { activeSection: detail.activeSection ?? 'company', sectionIds: detail.sectionIds ?? [] }
          : null,
      );
    };
    window.addEventListener(EMPLOYER_FORM_STATE_EVENT, onChange as EventListener);
    return () => window.removeEventListener(EMPLOYER_FORM_STATE_EVENT, onChange as EventListener);
  }, []);
  // Same guard as every block above: drop it the moment we leave the view, or
  // clicking Home leaves the form's section rows rendering over the dashboard.
  useEffect(() => {
    if (!isEmployersView && employerFormState) setEmployerFormState(null);
  }, [isEmployersView, employerFormState]);
  const employerFormSectionsOpen = isEmployersView && !!employerFormState;
  const employerFormTabItems = useMemo(
    () => buildEmployerFormSidebarTabItems(employerFormState?.sectionIds ?? []),
    [employerFormState?.sectionIds],
  );

  const { unreadCount } = useNotifications();
  const { unreadCount: unreadMessageCount } = useMessaging();
  // Compact mode threshold. The roomiest sidebar (super_admin: 20 nav
  // rows + header + profile) needs ~924 px at regular size — so any
  // viewport shorter than ~944 px would otherwise scroll/clip with the
  // regular row height. Engaging compact mode (32-px rows, h-14 header,
  // tighter spacers) at 950 px keeps every entry visible without
  // overflow on common laptop displays (13"/14" MacBooks live in the
  // 800–1000 px range once chrome and the dock are counted).
  const [isShortViewport, setIsShortViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight < 950 : false
  );

  const usesMainRailLabels = !isMobile && !usesTabletSidebarLayout && !NativeBridge.isRunningNatively();
  const hasContextNavigation = messagesSectionsOpen || showProjectTabs || showSystemSettingsTabs || showPreferencesTabs ||
    showProfileTabs || showEmailTabs || showWorkspacesTabs || salesSectionsOpen ||
    purchasesSectionsOpen || financeHubSectionsOpen ||
    employerSectionsOpen || employerFormSectionsOpen || userFormSectionsOpen ||
    interimSectionsOpen || hsSectionsOpen || takeoffToolsOpen || calendarSectionsOpen;
  const desktopToolbarBrand = usesMainRailLabels;
  const showContextAppRail = hasContextNavigation && desktopToolbarBrand;
  useDocumentFlag('data-desktop-toolbar-brand', desktopToolbarBrand);
  useDocumentFlag('data-context-rails', showContextAppRail);
  const standaloneMainRail = usesMainRailLabels && !hasContextNavigation;
  const usesSectionRailLabels = showContextAppRail && !messagesSectionsOpen &&
    !documentsNavigationOpen && !ramsDesktopOutlineOpen && !takeoffToolsOpen;
  const sectionRail = useSectionRailCollapsed(usesSectionRailLabels);
  const sectionRailCollapsed = usesSectionRailLabels && sectionRail.collapsed;
  // Rows lose their words when the rail collapses, so the tooltip is the label.
  const sectionRailTooltip = useCallback((label: React.ReactNode) => (
    sectionRailCollapsed ? { children: label, sideOffset: 8, hidden: false } : undefined
  ), [sectionRailCollapsed]);

  // Row density: the 28px icon wrapper + its vertical padding is what
  // actually sets row height (min-h- was dead weight, always smaller than
  // the rendered content). A pointer sidebar (mouse/trackpad) can run
  // tighter than the touch rail, which still needs a ~44/42px fingertip
  // target. Mobile drawers always use the touch density even when browser
  // device emulation reports a mouse pointer; otherwise their rows collapse
  // to the desktop density and leave most of the drawer unused. The runtime
  // check also covers touch rails at desktop widths (iPad landscape >= 1024px).
  const rowMetrics = useMemo(() => {
    const headingPad = isShortViewport ? 'pt-[8px] pb-[2px]' : 'pt-[10px] pb-[3px]';
    if (usesTabletSidebarLayout) {
      return {
        iconWrapH: 'h-[clamp(20px,3.2svh,24px)]',
        topRow: 'min-h-[clamp(27px,calc((100svh-160px)/16),42px)] py-[clamp(2px,0.7svh,6px)]',
        subRow: 'min-h-[clamp(26px,calc((100svh-160px)/17),38px)] py-[clamp(2px,0.55svh,5px)]',
        topIcon: 'h-[clamp(16px,2.4svh,19px)] w-[clamp(16px,2.4svh,19px)]',
        subIcon: 'h-[clamp(15px,2.2svh,18px)] w-[clamp(15px,2.2svh,18px)]',
        label: 'text-[clamp(11px,1.65svh,13px)] leading-[clamp(14px,2.1svh,17px)]',
        headingPad,
      };
    }
    if (isMobile || isTouchRuntime) {
      // The drawer scrolls so phone targets stay comfortable even on short screens.
      const projectList = restrictToProjectSections;
      return {
        iconWrapH: projectList ? 'h-[24px]' : 'h-[28px]',
        topRow: 'min-h-[44px] py-[8px]',
        subRow: 'min-h-[44px] py-[8px]',
        topIcon: projectList ? 'w-[18px] h-[18px]' : 'w-[20px] h-[20px]',
        subIcon: 'w-[18px] h-[18px]',
        label: 'text-[clamp(0.8125rem,3.5vw,0.875rem)] leading-[1.125rem]',
        headingPad,
      };
    }
    return {
      iconWrapH: 'h-[20px]',
      // Keep the same row pitch at both rail widths. The compact rail should
      // feel more open without shifting destinations when labels fade away.
      topRow: isShortViewport ? 'min-h-[32px] py-[6px]' : 'min-h-[36px] py-[8px]',
      subRow: isShortViewport ? 'min-h-[30px] py-[5px]' : 'min-h-[34px] py-[7px]',
      // The glyph extends 1px beyond its centring wrapper without changing
      // either rail state's row height.
      topIcon: 'w-[22px] h-[22px]',
      subIcon: 'w-[22px] h-[22px]',
      label: 'text-[14px] leading-[17px]',
      headingPad,
    };
  }, [isMobile, isTouchRuntime, isShortViewport, restrictToProjectSections, usesTabletSidebarLayout]);
  // The shared rail takes numbers where the rest of this sidebar takes classes; these
  // follow the same three row tiers as rowMetrics above.
  const takeoffRailMetrics = usesTabletSidebarLayout || (!isMobile && !isTouchRuntime && isShortViewport)
    ? RAIL_METRICS.compact
    : isMobile || isTouchRuntime ? RAIL_METRICS.touch : RAIL_METRICS.regular;
  const navMenuGap = isMobile || isTouchRuntime || usesTabletSidebarLayout
    ? isShortViewport ? 'gap-[1px]' : 'gap-[2px]'
    : isShortViewport ? 'gap-[3px]' : 'gap-[4px]';

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (theme === 'dark') return true;
    if (theme === 'light') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  useEffect(() => {
    if (theme === 'dark') { setIsDarkMode(true); return; }
    if (theme === 'light') { setIsDarkMode(false); return; }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsDarkMode(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);
  // The floating sidebar now uses Apple's neutral `regularMaterial` —
  // light in light mode, dark in dark mode — so the logo has to follow
  // the active theme instead of being pinned to the white-on-purple
  // variant. `logoIconDark` is the WHITE artwork (for dark surfaces);
  // `logoIconLight` is the DARK artwork (for light surfaces).
  const logoSrc = isDarkMode
    ? (logoIconDark ?? logoIconLight)
    : (logoIconLight ?? logoIconDark);
  const activeRoute = useMemo(() => (currentView || '').toLowerCase(), [currentView]);
  const isRouteActive = useCallback(
    (routeId: string) => activeRoute === routeId.toLowerCase(),
    [activeRoute]
  );
  const profileDisplayName =
    user?.display_name?.trim() ||
    user?.displayName?.trim() ||
    user?.name?.trim() ||
    user?.user_metadata?.name?.trim() ||
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
    user?.email?.split('@')[0] ||
    'User';
  const call = useOptionalCall();
  const callStatus = call?.status ?? 'idle';
  const callAvailability = call?.availability ?? 'offline';
  // Use the fully-hydrated DataService user for presence-specific fields.
  // Fall back to the prop in case the DataService hasn't loaded yet.
  const effectivePreferences = dsUser?.preferences ?? user?.preferences;
  const workingSchedule =
    effectivePreferences?.workingSchedule ??
    (dsUser as any)?.working_schedule ??
    (user as any)?.working_schedule ??
    null;

  const presence = usePresence({
    userId: user?.id,
    profileTimeZone: (dsUser as any)?.timezone ?? (user as any)?.timezone ?? null,
    serverManualEntry: effectivePreferences?.manualPresence ?? undefined,
    callStatus,
    callAvailability,
    workingSchedule,
    onPersist: useCallback(
      (entry) => {
        if (!user?.id) return;
        void updateUser(user.id, {
          preferences: {
            ...(effectivePreferences ?? {}),
            manualPresence: entry,
          },
        } as any);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [user?.id],
    ),
  });
  const presenceTitle = presence.source === 'manual'
    ? `${PRESENCE_LABELS[presence.status]} (manual)`
    : PRESENCE_LABELS[presence.status];
  const isPresenceAnimating = callStatus === 'incoming' || callStatus === 'outgoing';
  const activeSubnavParentId = showProjectTabs
    ? 'projects'
    : showSystemSettingsTabs
      ? 'system-settings'
      : showPreferencesTabs
        ? 'preferences'
      : showProfileTabs
        ? 'profile'
        : showEmailTabs
        ? 'email'
        : showWorkspacesTabs
          ? 'workspaces'
          : null;

  // A deep-linked or newly selected destination can begin below the fold in
  // data-driven sidebars (for example, the final bill in Pricing). Reveal the
  // active row within the sidebar only; `scrollIntoView()` is avoided because
  // it can also move the page canvas when the rail sits inside another
  // scrolling shell.
  const activeSidebarScrollKey = [
    currentView,
    activeSubnavParentId,
    projectActiveTab,
    boqNavigationState?.activeBillId,
    systemSettingsActiveTab,
    preferencesActiveTab,
    profileActiveTab,
    emailActiveTab,
    workspacesActiveTab,
    payrollActiveSection,
    payrollProfileState.activeTab,
    accountingActiveSection,
    taxActiveSection,
    salesActiveSection,
    purchasesActiveSection,
  ].join('|');
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const region = sidebarScrollRegionRef.current;
      const activeRows = region?.querySelectorAll<HTMLElement>('[data-sidebar="menu-button"][data-active="true"]');
      // Finance marks both its area and subsection active. Reveal the current
      // subsection, which can sit below the fold in the labelled desktop rail.
      const activeRow = activeRows?.[usesSectionRailLabels ? activeRows.length - 1 : 0];
      if (!region || !activeRow) return;

      const regionRect = region.getBoundingClientRect();
      const rowRect = activeRow.getBoundingClientRect();
      const inset = 4;
      if (rowRect.top < regionRect.top + inset) {
        region.scrollTo({ top: region.scrollTop + rowRect.top - regionRect.top - inset });
      } else if (rowRect.bottom > regionRect.bottom - inset) {
        region.scrollTo({ top: region.scrollTop + rowRect.bottom - regionRect.bottom + inset });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [activeSidebarScrollKey, usesSectionRailLabels, isShortViewport]);

  const getInlineSubnavItems = useCallback((parentId: string) => {
    if (parentId === 'projects' && showProjectTabs) {
      return projectNavigationItems;
    }
    if (parentId === 'system-settings' && showSystemSettingsTabs) {
      return systemSettingsTabItems;
    }
    if (parentId === 'preferences' && showPreferencesTabs) {
      return preferencesTabItems;
    }
    if (parentId === 'profile' && showProfileTabs) {
      return profileTabItems;
    }
    if (parentId === 'email' && showEmailTabs) {
      return emailTabItems;
    }
    if (parentId === 'workspaces' && showWorkspacesTabs) {
      // Inside a workspace, the section nav (Organisation / Subscription / ...)
      // takes over the focused-mode slot. The "Workspaces" back item lives at
      // the top of that list so the sidebar always offers a way out.
      if (currentView === 'add-workspace') return workspacesTabItems;
      return workspaceDetailContext ? workspaceDetailTabItems : workspacesTabItems;
    }
    return [];
  }, [
    workspacesTabItems,
    currentView,
    workspaceDetailContext,
    workspaceDetailTabItems,
    projectNavigationItems,
    showWorkspacesTabs,
    showProjectTabs,
    showSystemSettingsTabs,
    systemSettingsTabItems,
    showPreferencesTabs,
    preferencesTabItems,
    showProfileTabs,
    profileTabItems,
    showEmailTabs,
    emailTabItems,
  ]);

  const isSidebarItemActive = useCallback((itemId: string) => {
    if (isAccountingDestination(itemId) && isAccountingDestination(currentView)) return true;
    if (isContactsHubDestination(itemId) && isContactsHubView(currentView)) return true;
    const railDestination = mainNavigationDestination(currentView) ?? currentView;
    const onEnquiriesUnderOutreach = railDestination === 'pipeline' && enquiriesUnderOutreach;
    if (itemId === 'outreach' && onEnquiriesUnderOutreach) return true;
    if (isPipelineDestination(itemId) && isPipelineDestination(railDestination) && !onEnquiriesUnderOutreach) return true;
    if (hsEditorSectionsOpen) {
      return itemId === `${HS_EDITOR_SECTION_PREFIX}${hsEditorState?.activeSection ?? ''}`;
    }
    if (hsDocumentSectionsOpen) {
      return itemId === `${HS_DOCUMENT_SECTION_PREFIX}${hsDocumentState?.activeSection ?? ''}`;
    }
    // While the interim sections drill-in is showing, active state tracks
    // the application's own activeSection (from the bridge above) instead
    // of currentView — the sidebar's row ids for this mode are prefixed
    // INTERIM_SECTION_PREFIX and never match a real AppView.
    if (interimSectionsOpen) {
      return itemId === `${INTERIM_SECTION_PREFIX}${interimAppState?.activeSection ?? ''}`;
    }
    // Same treatment for the edit-user form's sections.
    if (userFormSectionsOpen) {
      return itemId === `${USER_FORM_SECTION_PREFIX}${userFormState?.activeTab ?? ''}`;
    }
    // Checked before the profile: the form is one level deeper, so it wins
    // whenever both channels look open.
    if (employerFormSectionsOpen) {
      return itemId === `${EMPLOYER_FORM_TAB_PREFIX}${employerFormState?.activeSection ?? ''}`;
    }
    if (employerSectionsOpen) {
      return itemId === `${EMPLOYER_DETAIL_TAB_PREFIX}${employerActiveSection}`;
    }
    if (itemId === 'rail-alerts') return currentView === 'notifications';
    if (itemId === 'rail-support') return currentView === 'support';
    if (itemId === activeSubnavParentId) {
      return true;
    }
    return isRouteActive(itemId);
  }, [currentView, enquiriesUnderOutreach, activeSubnavParentId, isRouteActive, hsEditorSectionsOpen, hsEditorState?.activeSection, hsDocumentSectionsOpen, hsDocumentState?.activeSection, interimSectionsOpen, interimAppState?.activeSection, userFormSectionsOpen, userFormState?.activeTab, employerSectionsOpen, employerActiveSection, employerFormSectionsOpen, employerFormState?.activeSection]);

  // Group heading: small, all-caps, muted — Apple System Settings sidebar
  // pattern. Non-interactive, no icon, generous top padding to separate the
  // group from whatever sits above it. Shared by the sub-nav rows and the
  // employer drill-in, both of which render grouped section lists.
  const renderSectionHeading = useCallback((key: string, label: string) => {
    // Project navigation on touch surfaces is already made of explicit,
    // labelled destinations. Category headings add four non-interactive rows
    // and push the final destinations behind the account footer on tablets,
    // so reserve the available height for the actual navigation targets.
    if (restrictToProjectSections || (usesTabletSidebarLayout && showProjectTabs)) return null;
    return (
      <SidebarMenuItem key={key} data-slot="app-sidebar-section-heading" className="list-none">
        <div
          data-slot="app-sidebar-section-label"
          className={cn(
            'font-apple px-2 text-[10px] font-semibold uppercase tracking-wider text-nav-section-heading',
            rowMetrics.headingPad
          )}
          style={{ fontFamily: 'var(--font-apple)' }}
        >
          {label}
        </div>
      </SidebarMenuItem>
    );
  }, [restrictToProjectSections, rowMetrics, showProjectTabs, usesTabletSidebarLayout]);

  const renderSubnavButton = useCallback((parentId: string, tab: any) => {
    const isProjectTab = parentId === 'projects';
    const isSettingsTab = parentId === 'system-settings';
    const isPreferencesTab = parentId === 'preferences';
    const isProfileTab = parentId === 'profile';
    const isEmailTab = parentId === 'email';
    const isClientTab = parentId === 'workspaces';
    const isPayrollTab = parentId === 'payroll';
    const isAccountingTab = parentId === 'accounting';
    const isTaxTab = parentId === 'tax';
    const isPipelineTab = parentId === 'pipeline';
    const isCalendarTab = parentId === 'calendar';
    const isWorkspaceDetailMode = isClientTab && !!workspaceDetailContext;
    // Profile and Settings tabs can both appear in the account-area sidebar at
    // once (see the focused render), so their active state must be gated on
    // which view is actually showing — otherwise the Profile "Overview" tab
    // would read active while you are in a Settings section, and vice versa.
    const isBoqView = isProjectTab && tab.navigationKind === 'boq-view';
    const isActive = isCalendarTab ? calendarActiveView === tab.id : isPipelineTab
      ? pipelineNavigation?.activeStage === tab.id
      : isClientTab && currentView === 'add-workspace'
      ? tab.id === 'clients'
      : isBoqView
      ? boqNavigationState?.activeBillId === tab.id
      : isProjectTab
      ? (projectActiveTab === tab.id || (tab.id === 'reports' && isProjectReportsTab(projectActiveTab)))
      : isSettingsTab
        ? (isSystemSettingsView && activeSystemSettingsTabId === tab.id)
        : isPreferencesTab
        ? (isPreferencesView && preferencesActiveTab === tab.id)
        : isProfileTab
          ? (isProfileView && (profileActiveTab === tab.id || (profileActiveTab ?? '').startsWith(`${tab.id}/`)))
          : isEmailTab
            ? emailActiveTab === tab.id
            : isWorkspaceDetailMode
              ? workspaceDetailContext?.activeTab === tab.id
              : isPayrollTab
                ? (payrollProfileState.open
                    ? payrollProfileState.activeTab === tab.id
                    : payrollActiveSection === tab.id)
                : isAccountingTab
                  ? accountingActiveSection === tab.id
                  : isTaxTab
                    ? taxActiveSection === tab.id
                : workspacesActiveTab === tab.id;

    const handleClick = () => {
      if (isCalendarTab) {
        window.dispatchEvent(new CustomEvent('calendar:set-view', { detail: { view: tab.id } }));
      } else if (isPipelineTab) {
        window.dispatchEvent(new CustomEvent(PIPELINE_SELECT_STAGE, { detail: { stage: tab.id } }));
      } else if (isBoqView) {
        window.dispatchEvent(new CustomEvent('boq:select-bill', {
          detail: { billId: tab.id },
        }));
      } else if (isProjectTab) {
        onProjectTabChange?.(tab.id);
      } else if (isSettingsTab) {
        // The account area lists Settings sections while you may be in the
        // Profile view (or vice versa). The tab-change callback only sets the
        // active tab; it does not switch views. So switch first when needed,
        // otherwise clicking a Settings section from Profile changes nothing.
        if (!isSystemSettingsView) onNavigate?.('system-settings');
        onSystemSettingsTabChange?.(tab.id);
      } else if (isPreferencesTab) {
        onPreferencesTabChange?.(tab.id);
      } else if (isProfileTab) {
        const targetView = tab.id.startsWith('workspace/') ? 'profile' : 'account';
        if (currentView !== targetView) onNavigate?.(targetView);
        onProfileTabChange?.(tab.id);
      } else if (isEmailTab) {
        onEmailTabChange?.(tab.id);
      } else if (isClientTab) {
        if (currentView === 'add-workspace') {
          onWorkspacesTabChange?.(tab.id);
        } else if (isWorkspaceDetailMode) {
          window.dispatchEvent(
            new CustomEvent('workspaces:detail-set-tab', { detail: { tab: tab.id } })
          );
        } else {
          onWorkspacesTabChange?.(tab.id);
        }
      } else if (isPayrollTab) {
        if (payrollProfileState.open) {
          window.dispatchEvent(new CustomEvent('payroll:profile-tab-change', {
            detail: { tab: tab.id },
          }));
        } else {
          window.dispatchEvent(new CustomEvent('payroll:navigate-section', {
            detail: { sectionId: tab.id },
          }));
        }
      } else if (isAccountingTab) {
        window.dispatchEvent(new CustomEvent('accounting:navigate-section', {
          detail: { sectionId: tab.id },
        }));
      } else if (isTaxTab) {
        window.dispatchEvent(new CustomEvent('tax:navigate-section', {
          detail: { sectionId: tab.id },
        }));
      }
      setOpenMobile(false);
    };

    const badgeCount =
      tab.id === 'requests'
        ? workspacesStats?.pendingRequests
        : tab.badge;
    const hasBadge = typeof badgeCount === 'number' && badgeCount > 0;
    const accessibleLabel = hasBadge ? `${tab.label} (${badgeCount})` : tab.label;

    if (tab.kind === 'heading') {
      // Keep the project workspace as one visible list of destinations.
      // Section labels added visual breaks without changing where anything led.
      if (isProjectTab) return null;
      return renderSectionHeading(`${parentId}:${tab.id}`, tab.label);
    }

    return (
      <SidebarMenuItem key={`${parentId}:${tab.id}`}>
        <SidebarMenuButton
          data-id={`${parentId}:${tab.id}`}
          data-section-rail-item={usesSectionRailLabels || undefined}
          onClick={handleClick}
          isActive={isActive}
          aria-label={accessibleLabel}
          tooltip={usesSectionRailLabels ? sectionRailTooltip(accessibleLabel) : collapsedSidebarTooltip(accessibleLabel)}
          className={cn(
            // Sub-nav rows sit flush with the parent — no indent. The
            // section-focus filter (only the active parent + its children
            // render) makes the relationship implicit, so the visual
            // step-down was redundant chrome competing with the labels.
            NAV_ITEM_ROW,
            rowMetrics.subRow
          )}
        >
          <div className={cn("relative w-[28px] shrink-0 flex items-center justify-center", rowMetrics.iconWrapH)}>
            <tab.icon className={`${rowMetrics.subIcon} shrink-0 transition-colors ${isActive ? 'text-nav-item-active-text' : 'text-nav-item-text group-hover:text-nav-item-text-hover'}`} />
            {hasBadge && (
              <span
                data-slot="app-sidebar-count-indicator"
                aria-hidden="true"
                className="absolute -right-0.5 top-0 hidden size-1.5 rounded-full bg-sidebar-foreground ring-2 ring-sidebar"
              />
            )}
          </div>
          <span
            data-slot="section-rail-label"
            className={`font-apple ${rowMetrics.label} min-w-0 flex-1 truncate font-medium ${isActive ? 'text-nav-item-active-text font-semibold' : ''}`}
            style={{ fontFamily: 'var(--font-apple)' }}
          >
            {tab.label}
          </span>
          {hasBadge && (
            <Badge variant="secondary" aria-hidden="true" className="ml-auto min-w-5 border-transparent bg-sidebar-foreground/15 px-1.5 py-0 text-[10px] leading-none tabular-nums text-sidebar-foreground">
              {badgeCount > 99 ? '99+' : badgeCount}
            </Badge>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }, [
    workspacesActiveTab,
    workspaceDetailContext,
    workspacesStats?.pendingRequests,
    onWorkspacesTabChange,
    onProjectTabChange,
    onSystemSettingsTabChange,
    onPreferencesTabChange,
    onProfileTabChange,
    onEmailTabChange,
    emailActiveTab,
    boqNavigationState?.activeBillId,
    projectActiveTab,
    setOpenMobile,
    activeSystemSettingsTabId,
    profileActiveTab,
    isSystemSettingsView,
    isPreferencesView,
    preferencesActiveTab,
    isProfileView,
    payrollActiveSection,
    payrollProfileState,
    accountingActiveSection,
    taxActiveSection,
    pipelineNavigation?.activeStage,
    calendarActiveView,
    onNavigate,
    onShowProfile,
    rowMetrics,
    isSidebarCollapsed,
    isTouchRuntime,
    usesTabletSidebarLayout,
    collapsedSidebarTooltip,
    renderSectionHeading,
    usesSectionRailLabels,
  ]);

  const mainRailPrefs = useMainRailPreferences(user?.id ?? '');
  const [customiseRailOpen, setCustomiseRailOpen] = useState(false);
  const mainRailLabel = useMainRailLabelMode(user?.id ?? '');
  // Tablets always get the icon rail: a finger needs no label to aim at once the tile carries a 44px
  // ring, and the width goes back to the page. The saved choice is kept for the user's mouse devices.
  const mainRailLabelModeLocked = useDesktopFrameTouch();
  const secondRailHidden = useTabletSecondRail(mainRailLabelModeLocked && showContextAppRail);
  const mainRailIconsOnly = usesMainRailLabels && (mainRailLabelModeLocked || mainRailLabel.mode === 'icons-only');
  // Moves read the rail as drawn this render, which is assembled further down.
  const railMoveRef = useRef<{ canMove: (id: string, delta: -1 | 1) => boolean; move: (id: string, delta: -1 | 1) => void }>({ canMove: () => false, move: () => {} });
  const mainRailMenuActions = useMemo<MainRailMenuActions>(() => ({
    onHide: (id) => mainRailPrefs.setHidden(id, true),
    canMove: (id, delta) => railMoveRef.current.canMove(id, delta),
    onMove: (id, delta) => railMoveRef.current.move(id, delta),
    onCustomise: () => setCustomiseRailOpen(true),
    labelMode: mainRailLabel.mode,
    onLabelModeChange: mainRailLabel.setMode,
    labelModeLocked: mainRailLabelModeLocked,
  }), [mainRailPrefs.setHidden, mainRailLabel.mode, mainRailLabel.setMode, mainRailLabelModeLocked]);

  // The rail width token feeds the full-bleed canvas inset, so the narrower rail has to move it too.
  useEffect(() => {
    if (!mainRailIconsOnly) return;
    const shell = document.querySelector<HTMLElement>('.qs-shell-root');
    if (!shell) return;
    shell.style.setProperty('--qs-primary-rail-width', '48px');
    return () => { shell.style.removeProperty('--qs-primary-rail-width'); };
  }, [mainRailIconsOnly]);

  const { mainNavItems, crmNavItems, adminNavItems, menuGrants, accountingDestinations } = useMemo(() => {
    const groups = getSidebarNavGroups({
      role: user?.role ?? 'member',
      internalWorkspace: workspaceMainNavigation.internalWorkspace,
      resolvedItems: workspaceMainNavigation.authoritative ? workspaceMainNavigation.navigation?.main ?? null : undefined,
      unreadMessageCount,
      unreadEmailCount: emailInboxUnread,
      voiceLine,
    });

    return {
      menuGrants: groups.menuGrants,
      accountingDestinations: groups.accountingDestinations,
      mainNavItems: withQuantisAINavigation(groups.mainNavItems),
      crmNavItems: groups.crmNavItems,
      adminNavItems: groups.adminNavItems.map((item) => ({
        ...item,
        onClick:
          item.id === 'user-management'
            ? onShowUserManagement
            : item.id === 'workspaces'
              ? onShowWorkspaces
              : item.id === 'supervisor-management'
                ? onShowAdminSupervisorManagement
                : undefined,
      })),
    };
  }, [
    onShowAdminSupervisorManagement,
    onShowWorkspaces,
    onShowUserManagement,
    unreadMessageCount,
    emailInboxUnread,
    user?.role,
    workspaceMainNavigation.authoritative,
    workspaceMainNavigation.internalWorkspace,
    workspaceMainNavigation.navigation,
    voiceLine,
  ]);

  const renderSidebarButton = useCallback((item: any, onClick: () => void, iconOnly = false, primaryDestination = false, shortcut?: MainRailShortcut & { hintVisible: boolean }) => {
    const isMainRailItem = usesMainRailLabels && (iconOnly || primaryDestination);
    const hideLabel = isMainRailItem && mainRailIconsOnly;
    const badgedLabel = typeof item.badge === 'number' && item.badge > 0 ? `${item.label} (${item.badge})` : item.label;
    const railShortcut = isMainRailItem ? shortcut : undefined;
    // Tooltips name the destination only. The shortcuts still work, are announced through
    // aria-keyshortcuts, and show on demand while the modifier is held.
    const mainRailTooltip = hideLabel ? badgedLabel : undefined;
    // Both forms are always in the DOM; CSS shows the filled one on hover and focus-visible
    // (see main-rail-icons.css), so the swap costs no render and never marks the current page.
    const FilledIcon = isMainRailItem ? filledIconFor(item.icon) : undefined;
    const swapsToFilled = FilledIcon !== undefined && FilledIcon !== item.icon;
    const iconClassName = `${rowMetrics.topIcon} shrink-0 transition-colors ${!iconOnly && isSidebarItemActive(item.id) ? 'text-nav-item-active-text' : 'text-nav-item-text group-hover:text-nav-item-text-hover'}`;
    const pipelineChoices = item.id === 'outreach'
      ? outreachMenuItems(user?.role, workspaceMainNavigation.internalWorkspace === true, menuGrants)
      : isPipelineDestination(item.id) ? pipelineMenuItems(user?.role, workspaceMainNavigation.internalWorkspace === true, menuGrants) : [];
    const button = (
        <SidebarMenuButton
          data-id={item.id}
          data-main-rail-item={usesMainRailLabels && (iconOnly || primaryDestination) || undefined}
          data-section-rail-item={usesSectionRailLabels && !iconOnly && !primaryDestination || undefined}
          onClick={pipelineChoices.length ? undefined : onClick}
          isActive={isSidebarItemActive(item.id)}
          aria-current={isSidebarItemActive(item.id) ? 'page' : undefined}
          aria-label={badgedLabel}
          aria-keyshortcuts={railShortcut?.ariaKeyShortcuts}
          tooltip={hideLabel ? { children: mainRailTooltip, side: 'right', sideOffset: 8, hidden: false } : isMainRailItem ? undefined : usesSectionRailLabels ? sectionRailTooltip(typeof item.badge === 'number' && item.badge > 0 ? `${item.label} (${item.badge})` : item.label) : iconOnly ? { children: item.label, sideOffset: 8, hidden: false } : collapsedSidebarTooltip(typeof item.badge === 'number' && item.badge > 0 ? `${item.label} (${item.badge})` : item.label)}
          className={cn(
            // Inner padding `px-2` (8 px) so the icon mark's left edge
            // sits at `12 px (SidebarContent px-3) + 8 px = 20 px` from
            // the sidebar's edge — same X as the brand logo (`px-5` = 20)
            // and the profile avatar (`px-3 + p-2` = 20). The 28×28 icon
            // wrapper below + `gap-[12px]` mirror the brand row so the
            // text label of every nav row also lands at the same X as
            // the "Quantis" wordmark and the profile name.
            NAV_ITEM_ROW_COLLAPSIBLE,
            rowMetrics.topRow
          )}
        >
          <div className={cn("relative w-[28px] shrink-0 flex items-center justify-center", rowMetrics.iconWrapH)}>
            <item.icon data-rail-icon={swapsToFilled ? 'outline' : undefined} className={iconClassName} />
            {swapsToFilled && <FilledIcon data-rail-icon="filled" className={`${iconClassName} absolute inset-0 m-auto`} />}
            {typeof item.badge === 'number' && item.badge > 0 && (
              <span data-slot="app-sidebar-count-indicator" aria-hidden="true" className="absolute -right-0.5 top-0 hidden size-1.5 rounded-full bg-sidebar-foreground ring-2 ring-sidebar" />
            )}
            {railShortcut?.hintVisible && <MainRailShortcutHint number={railShortcut.number} />}
          </div>
          {!hideLabel && <span data-slot={usesSectionRailLabels && !iconOnly && !primaryDestination ? "section-rail-label" : "main-rail-label"} className={`font-apple ${rowMetrics.label} min-w-0 flex-1 ${!iconOnly && (item.id.startsWith(HS_DOCUMENT_SECTION_PREFIX) || item.id.startsWith(HS_EDITOR_SECTION_PREFIX)) ? '!whitespace-normal !text-clip break-words text-left' : 'truncate'} font-medium ${!iconOnly && isSidebarItemActive(item.id) ? 'text-nav-item-active-text font-semibold' : ''}`} style={{ fontFamily: 'var(--font-apple)' }}>
            {item.label}
          </span>}
          {typeof item.badge === 'number' && item.badge > 0 && (
            <Badge variant="secondary" aria-hidden="true" className="ml-auto min-w-5 border-transparent bg-sidebar-foreground/15 px-1.5 py-0 text-[10px] leading-none tabular-nums text-sidebar-foreground">
              {item.badge > 99 ? '99+' : item.badge}
            </Badge>
          )}
        </SidebarMenuButton>
    );
    const row = (
      <SidebarMenuItem onContextMenu={isMainRailItem ? (event: React.MouseEvent) => event.stopPropagation() : undefined}>
        {pipelineChoices.length > 0 ? <DropdownMenu key={`${user?.id}:${workspaceMainNavigation.navigation?.workspaceId}`} onOpenChange={open => {
          if (open && item.id === 'outreach' && workspaceMainNavigation.navigation?.workspaceId) void warmOutreachSections(workspaceMainNavigation.navigation.workspaceId);
        }}>
          <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" sideOffset={10} className="w-60" aria-label={`${item.id === 'outreach' ? 'Outreach' : 'Projects'} options`}>
          {pipelineChoices.map(choice => <DropdownMenuItem key={choice.id} className="min-h-11" onSelect={() => {
            if (choice.destination === 'outreach') window.dispatchEvent(new CustomEvent(currentView === 'outreach' ? OUTREACH_SET_SECTION_EVENT : OUTREACH_OPEN_SECTION_EVENT, {detail: {sectionId: choice.id}}));
            else onNavigate(choice.destination);
            if (isMobile) setOpenMobile(false);
          }}>{choice.label}</DropdownMenuItem>)}
        </DropdownMenuContent>
        </DropdownMenu> : button}
      </SidebarMenuItem>
    );
    return (
      <React.Fragment key={item.id}>
        {usesMainRailLabels && (iconOnly || primaryDestination) && !item.railFixed ? (
          <MainRailContextMenu itemId={item.id} label={item.label} actions={mainRailMenuActions}>{row}</MainRailContextMenu>
        ) : row}
        {!iconOnly && !isPipelineDestination(item.id) && item.id !== 'outreach' && getInlineSubnavItems(item.id).map((tab) => renderSubnavButton(item.id, tab))}
      </React.Fragment>
    );
  }, [mainNavItems, user?.id, user?.role, workspaceMainNavigation.internalWorkspace, workspaceMainNavigation.navigation?.workspaceId, menuGrants, currentView, onNavigate, isMobile, setOpenMobile, collapsedSidebarTooltip, getInlineSubnavItems, isSidebarItemActive, mainRailIconsOnly, mainRailMenuActions, renderSubnavButton, rowMetrics, sectionRailTooltip, usesMainRailLabels, usesSectionRailLabels]);

  const profileTriggerButton = (
    <button
      type="button"
      data-slot="app-sidebar-profile-trigger"
      className={cn(
        // 12 px gap mirrors the brand-row spacing
        // (`app-sidebar-brand` uses `gap-[12px]`) so the profile
        // name sits at the same X as the "Quantis" wordmark and
        // every nav-row label.
        "w-full min-h-11 flex items-center gap-[12px] cursor-pointer hover:bg-nav-item-hover-bg rounded-[var(--radius)] transition-colors text-left",
        isShortViewport ? "p-1.5" : "p-2",
        profileOpen && "bg-nav-item-hover-bg",
      )}
    >
          <div className="relative w-[28px] h-[28px] shrink-0 flex items-center justify-center">
            <UserAvatar
              src={user?.avatar}
              name={profileDisplayName}
              size={28}
              border="1px solid rgba(255,255,255,0.1)"
              fallbackBg="rgba(255,255,255,0.1)"
              fallbackColor="var(--sidebar-foreground)"
            />
            {/* No tooltip of its own: the profile row's tooltip carries the
                presence line, and a second callout in the same spot read as
                a glitch. */}
            <span
              style={{ background: 'var(--sidebar-background)' }}
              className={cn(
                'absolute bottom-0 right-0 rounded-full flex items-center justify-center p-[1.5px]',
                isPresenceAnimating && 'animate-pulse',
              )}
            >
              <PresenceDot status={presence.status} size={8} />
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-nav-item-text-hover font-apple text-[13px] font-medium truncate block" style={{ fontFamily: 'var(--font-apple)' }}>
              {profileDisplayName}
            </span>
          </div>
          <ChevronDown className={cn(
            "w-3.5 h-3.5 text-nav-item-text transition-transform duration-200 shrink-0",
            profileOpen && "rotate-180",
          )} />
        </button>
  );

  const profileSection = (
    <section data-slot="app-sidebar-footer-profile" className={cn(
      "bg-transparent",
      isShortViewport ? "px-2 pb-2 pt-1.5" : "px-3 pb-3 pt-2"
    )}>
      <Popover open={profileOpen} onOpenChange={setProfileOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{profileTriggerButton}</PopoverTrigger>
          </TooltipTrigger>
          {isSidebarCollapsed && !isTouchRuntime && !usesTabletSidebarLayout && (
            <TooltipContent side="right" align="center" sideOffset={8}>
              {profileDisplayName}
              <span className="block text-[11px] opacity-80">{presenceTitle}</span>
            </TooltipContent>
          )}
        </Tooltip>

        {/* Account menu: the same card as every other menu (the --menu-* and
            --ui-menu-* tokens), so it follows the palette and shape changes. */}
        <PopoverContent
          side="right"
          align="end"
          sideOffset={10}
          className="w-[260px] rounded-[var(--ui-menu-radius)] border-0 px-[var(--menu-padding-x)] py-[var(--menu-padding-y)] shadow-[var(--ui-menu-shadow)]"
        >
          <div className="flex min-h-[46px] items-center gap-3 px-2.5 py-1.5">
            <div className="relative shrink-0">
              <UserAvatar
                src={user?.avatar}
                name={profileDisplayName}
                size={32}
                border="1px solid var(--border)"
                fallbackBg="var(--muted)"
                fallbackColor="var(--muted-foreground)"
              />
              {/* The ring punches the presence dot out of the card surface. */}
              <span
                style={{ background: 'var(--popover)' }}
                className="absolute -bottom-0.5 -right-0.5 rounded-full p-[2px]"
              >
                <PresenceDot status={presence.status} size={9} />
              </span>
            </div>
            <div className="min-w-0">
              <p className="truncate text-[length:var(--menu-font-size)] text-popover-foreground">
                {profileDisplayName}
              </p>
              <p className="text-xs text-muted-foreground">
                {/* The avatar already wears the status dot; the label needs no second one. */}
                {PRESENCE_LABELS[presence.status]}
              </p>
            </div>
          </div>

          <div className="my-1 border-t border-border" />

          <PresenceStatusPicker
            currentStatus={presence.status}
            source={presence.source}
            onSelect={s => presence.setManualStatus(s)}
            isShortViewport={isShortViewport}
          />

          <div className="my-1 border-t border-border" />

          <button
            onClick={() => {
              onShowProfile();
              setProfileOpen(false);
              if (isMobile) setOpenMobile(false);
            }}
            className={ACCOUNT_MENU_ROW}
          >
            {/* Settings is one area (Account, Preferences and the rest in its
                second nav), so this single entry covers them all. */}
            <Settings className="size-4 shrink-0 text-muted-foreground" />
            <span>{ACCOUNT_MENU_LABELS.settings}</span>
          </button>
          <button
            onClick={() => { onLogout(); setProfileOpen(false); }}
            className={ACCOUNT_MENU_ROW}
          >
            <LogOut className="size-4 shrink-0 text-muted-foreground" />
            <span>{ACCOUNT_MENU_LABELS.signOut}</span>
          </button>
        </PopoverContent>
      </Popover>
    </section>
  );

  useEffect(() => {
    const handleResize = () => setIsShortViewport(window.innerHeight < 950);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Publish presence changes to AuthenticatedController so it can include the
  // current status in the next sidebarState send to the native shell.
  useEffect(() => {
    if (!NativeBridge.isMacShell()) return;
    const nativeId = PRESENCE_TO_NATIVE[presence.status];
    window.dispatchEvent(
      new CustomEvent('quantis:presence:status-change', { detail: { status: nativeId } }),
    );
  }, [presence.status]);

  // Handle set-status actions forwarded from the native account menu. The
  // native side sends a contract id; we map it back to an internal value before
  // calling the same setter the web popover uses.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ status: PresenceStatus }>).detail;
      if (detail?.status) {
        presence.setManualStatus(detail.status);
      }
    };
    window.addEventListener('quantis:presence:set-status', handler);
    return () => window.removeEventListener('quantis:presence:set-status', handler);
  // presence.setManualStatus is stable (useCallback with no deps), so it's safe
  // to omit from deps — but we include presence to satisfy the linter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The offcanvas drawer's opener. `setOpenMobile` lives behind useSidebar(),
  // which only components inside SidebarProvider can reach — the native
  // headerControl handler sits above it, so it asks over the window-event
  // bridge instead of having the setter threaded down to it.
  useEffect(() => {
    const open = () => setOpenMobile(true);
    window.addEventListener('sidebar:open-mobile', open);
    return () => window.removeEventListener('sidebar:open-mobile', open);
  }, [setOpenMobile]);


  /**
   * Initiates a native window drag from the sidebar header strip.
   * Only fires in the macOS shell, only on primary-button press, and only when
   * the pointer is not over an interactive descendant (buttons, links, inputs).
   */
  const handleSidebarHeaderDrag = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (!NativeBridge.isMacShell()) return;
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button, a, input, textarea, select, [role="button"], [data-no-window-drag]')) return;
    window.QuantisNativeBridge?.send?.('beginWindowDrag', {});
  }, []);

  const handleItemClick = (item: { id: string; onClick?: () => void }) => {
    if (item.onClick) {
      item.onClick();
    } else {
      onNavigate(item.id);
    }
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // The sidebar participates in the shell's flex layout, so animating its
  // rendered width moves the content canvas on the exact same frame. CSS
  // formerly hid labels immediately and then animated width separately,
  // producing the visible first-frame jump. One Web Animations timeline now
  // owns width, tablet margins/opacity and label fading in both directions.
  useLayoutEffect(() => {
    const sidebar = sidebarElementRef.current;
    const start = sidebarStartGeometryRef.current;
    if (!sidebar || isMobile) return;
    if (standaloneMainRail || usesSectionRailLabels) {
      sidebarStartGeometryRef.current = null;
      sidebarAnimationRef.current?.cancel();
      sidebarContentAnimationsRef.current.forEach((animation) => animation.cancel());
      sidebarContentAnimationsRef.current = [];
      return;
    }
    if (!start) return;
    sidebarStartGeometryRef.current = null;

    sidebarAnimationRef.current?.cancel();
    sidebarContentAnimationsRef.current.forEach((animation) => animation.cancel());
    sidebarContentAnimationsRef.current = [];

    // Cancelling exposes the target geometry already supplied by this render.
    const targetStyles = getComputedStyle(sidebar);
    const target = {
      width: sidebar.getBoundingClientRect().width,
      marginLeft: Number.parseFloat(targetStyles.marginLeft) || 0,
      marginRight: Number.parseFloat(targetStyles.marginRight) || 0,
      opacity: Number.parseFloat(targetStyles.opacity) || 1,
    };
    // Context navigation shares the Inspector reflow timing so both layout
    // widths settle together. Standalone navigation keeps its slide timing.
    const motion = getShellPanelMotionTiming(showContextAppRail ? undefined : 'slide');
    const duration = motion.reducedMotion ? 0 : motion.duration;

    if (duration === 0) return;

    const shellAnimation = sidebar.animate(
      [
        {
          width: `${start.width}px`,
          marginLeft: `${start.marginLeft}px`,
          marginRight: `${start.marginRight}px`,
          opacity: start.opacity,
        },
        {
          width: `${target.width}px`,
          marginLeft: `${target.marginLeft}px`,
          marginRight: `${target.marginRight}px`,
          opacity: target.opacity,
        },
      ],
      { duration, easing: motion.easing, fill: 'both' },
    );
    sidebarAnimationRef.current = shellAnimation;

    const labelNodes = sidebar.querySelectorAll<HTMLElement>([
      "[data-slot='app-sidebar-brand-label']",
      "[data-slot='sidebar-menu-button'] > span:not(:first-child)",
      "[data-sidebar='menu-button'] > span:not(:first-child)",
      '.qs-sidebar-label',
      "[data-slot='app-sidebar-section-label']",
      "[data-sidebar='group-label']",
      "[data-slot='app-sidebar-profile-trigger'] > *:not(:first-child)",
    ].join(','));
    const contentDuration = Math.round(duration * 0.7);
    const contentDelay = isSidebarCollapsed ? 0 : Math.round(duration * 0.18);
    const fadingLabels = Array.from(labelNodes).filter((node) => !node.matches('[data-slot="main-rail-label"]') || !node.closest('[data-labelled-main-rail="true"]'));
    sidebarContentAnimationsRef.current = Array.from(fadingLabels, (node) => node.animate(
      isSidebarCollapsed
        ? [{ opacity: 1 }, { opacity: 0 }]
        : [{ opacity: 0 }, { opacity: 1 }],
      {
        duration: contentDuration,
        delay: contentDelay,
        easing: motion.easing,
        fill: 'both',
      },
    ));

    // Animate the same selection surface as the rail, rather than replacing
    // an expanded button background with a circle on the first frame.
    sidebar.querySelectorAll<HTMLElement>("[data-slot='sidebar-menu-button']").forEach((button) => {
      if (button.matches('[data-main-rail-item]') && button.closest('[data-labelled-main-rail="true"]')) return;
      const expanded = {
        width: `${Math.max(38, (isSidebarCollapsed ? start.width : target.width) - 24)}px`,
        height: `${button.getBoundingClientRect().height}px`,
        borderRadius: '9px',
        left: '0px',
      };
      const circle = { width: '38px', height: '38px', borderRadius: '19px', left: '1px' };
      sidebarContentAnimationsRef.current.push(button.animate(
        isSidebarCollapsed ? [expanded, circle] : [circle, expanded],
        { duration, easing: motion.easing, fill: 'both', pseudoElement: '::before' },
      ));
    });

    shellAnimation.onfinish = () => {
      shellAnimation.cancel();
      sidebarContentAnimationsRef.current.forEach((animation) => animation.cancel());
      sidebarContentAnimationsRef.current = [];
      if (sidebarAnimationRef.current === shellAnimation) {
        sidebarAnimationRef.current = null;
      }
    };
  }, [isMobile, isSidebarCollapsed, detachedInspectorOpen, compactProjectViewport, showContextAppRail, standaloneMainRail, usesSectionRailLabels]);

  const focusToActiveSection = !restrictToProjectSections && isInSubContext && !!activeSubnavParentId;
  // Hiding only trims the rail; the open view and its destinations stay reachable.
  const notHidden = <T extends { id: string }>(list: T[]) => list.filter((i) => !mainRailPrefs.hiddenSet.has(i.id));
  // The person's own order applies to the rail and to the grouped list alike; groups keep their own members.
  const ordered = <T extends { id: string }>(list: T[]) => applyRailOrder(list, mainRailPrefs.order);
  const visibleMainNav = focusToActiveSection
    ? mainNavItems.filter((i) => i.id === activeSubnavParentId)
    : notHidden(ordered(mainNavItems));
  const visibleCrmNav = focusToActiveSection
    ? crmNavItems.filter((i) => i.id === activeSubnavParentId)
    : notHidden(ordered(crmNavItems));
  const visibleAdminNav = focusToActiveSection
    ? adminNavItems.filter((i) => i.id === activeSubnavParentId)
    : notHidden(ordered(adminNavItems));
  const inGroup = <T extends { id: string }>(list: T[], group: string) => list.map((item) => ({ ...item, group }));
  const customiseRailItems = orderByGroup([inGroup(mainNavItems, 'main'), inGroup(crmNavItems, 'crm'), inGroup(adminNavItems, 'admin')], mainRailPrefs.order);
  // A move from the rail's menu swaps the item with the next one shown in its own group, as the rail draws them.
  const shownBeside = (id: string) => {
    const group = customiseRailItems.find((item) => item.id === id)?.group;
    return (item: { id: string; group: string }) => item.group === group && !mainRailPrefs.hiddenSet.has(item.id);
  };
  railMoveRef.current = {
    canMove: (id, delta) => canMoveNavigationEntry(customiseRailItems, id, delta, shownBeside(id)),
    move: (id, delta) => mainRailPrefs.setOrder(moveNavigationEntry(customiseRailItems, id, delta, shownBeside(id)).map((item) => item.id)),
  };
  // Both rail layouts list the same items in this order, so the numbers match what is on screen.
  const mainRailItems = notHidden(customiseRailItems);
  const openProjectId = isProjectDetailView ? projectTabProps?.projectId : undefined;
  const recentProjects = useRecentProjects(
    user?.id,
    openProjectId && projectTabProps?.projectName
      ? { id: openProjectId, name: projectTabProps.projectName, section: projectActiveTab }
      : null,
  );
  // Rail tiles are tall, so the rail keeps to the three jobs an estimator juggles.
  const recentShown = visibleRecent(recentProjects, {
    limit: usesMainRailLabels ? 3 : 5,
    liveTitle: (item) => getProject?.(item.id)?.name,
    isAvailable: (item) => (projects?.length ? !!getProject(item.id) : undefined),
  });
  const renderRecentGroup = () => (
    <SidebarRecentGroup
      items={recentShown}
      activeId={openProjectId}
      variant={usesMainRailLabels ? 'rail' : 'list'}
      iconsOnly={mainRailIconsOnly}
      rowClassName={cn(NAV_ITEM_ROW_COLLAPSIBLE, rowMetrics.topRow)}
      iconWrapClassName={rowMetrics.iconWrapH}
      labelClassName={rowMetrics.label}
      headingPadClassName={rowMetrics.headingPad}
      onOpen={(item) => {
        openRecentProject(item);
        if (isMobile) setOpenMobile(false);
      }}
    />
  );
  // Tablets reach Alerts and Support from the rail instead of the header. Fixed entries: they are
  // not destinations, so they take no shortcut number and no Hide menu.
  const tabletRailAlertsItem = mainRailLabelModeLocked && onToggleNotifications
    ? { id: 'rail-alerts', label: 'Alerts', icon: Bell, badge: unreadCount, railFixed: true }
    : null;
  const tabletRailSupportItem = mainRailLabelModeLocked && onToggleSupport
    ? { id: 'rail-support', label: 'Support', icon: LifeBuoy, railFixed: true }
    : null;
  // Tablets open these as pages, so the entries navigate; the inspector toggles stay on the header
  // for mouse devices.
  const openTabletRailPage = (view: 'notifications' | 'support') => {
    onNavigate(view);
    if (isMobile) setOpenMobile(false);
  };
  const renderTabletRailAlerts = () => tabletRailAlertsItem
    && renderSidebarButton(tabletRailAlertsItem, () => openTabletRailPage('notifications'), true);
  const tabletRailSupport = tabletRailSupportItem ? (
    <SidebarMenu data-slot="app-sidebar-rail-support" className="px-3 pb-1">
      {renderSidebarButton(tabletRailSupportItem, () => openTabletRailPage('support'), true)}
    </SidebarMenu>
  ) : null;
  const mainRailShortcuts = useMainRailShortcuts(
    mainRailItems.map((item) => ({ id: item.id, onSelect: () => handleItemClick(item) })),
    usesMainRailLabels,
  );
  const railShortcutFor = (id: string) => {
    const shortcut = mainRailShortcuts.shortcutFor(id);
    return shortcut ? { ...shortcut, hintVisible: mainRailShortcuts.hintVisible } : undefined;
  };
  // A small gap where one default group gives way to the next; none once the person has set their own order.
  const renderRailWithGaps = <T extends { id: string }>(items: readonly T[], render: (item: T) => React.ReactNode) => {
    const ids = items.map((item) => item.id);
    return items.map((item, index) => (
      <React.Fragment key={item.id}>
        {startsNavigationGroup(ids, index, mainRailPrefs.order.length > 0)
          && <li aria-hidden="true" data-slot="main-rail-group-gap" className="h-2.5 shrink-0" />}
        {render(item)}
      </React.Fragment>
    ));
  };
  const customiseRailDialog = (
    <CustomiseRailDialog
      open={customiseRailOpen}
      onOpenChange={setCustomiseRailOpen}
      items={customiseRailItems}
      hiddenSet={mainRailPrefs.hiddenSet}
      onToggle={mainRailPrefs.setHidden}
      onOrderChange={mainRailPrefs.setOrder}
      onReset={mainRailPrefs.reset}
      canReset={mainRailPrefs.hiddenSet.size > 0 || mainRailPrefs.order.length > 0}
    />
  );
  return (
    <div data-slot={showContextAppRail ? "context-navigation-rails" : undefined} className={showContextAppRail ? "flex shrink-0 min-h-0" : "contents"}>
      {showContextAppRail && (
        <Sidebar collapsible="none" variant="sidebar" data-collapsed="true" data-desktop-toolbar-brand="true" data-slot="sidebar" data-labelled-main-rail="true" data-main-rail-label-mode={mainRailIconsOnly ? 'icons-only' : undefined} data-rail-lists-messages={mainRailItems.some((item) => item.id === 'messages') || undefined} aria-label="Main navigation" className="group/rail relative z-50 !shrink-0 overflow-visible mt-3 ml-2 mb-2 rounded-[16px] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.18),0_8px_24px_-8px_rgba(0,0,0,0.10)] !h-auto max-h-[calc(100svh-20px)] backdrop-blur-xl backdrop-saturate-150" style={{ width: 'var(--qs-primary-rail-width, 70px)', background: 'var(--sidebar)' }}>
        <SidebarHeader className="p-0 shrink-0 border-none bg-transparent relative z-20">

          <header
            data-slot="app-sidebar-top-row"
            data-quantis-window-drag="true"
            onMouseDown={handleSidebarHeaderDrag}
            className={cn(
            "flex items-center justify-between relative bg-inherit",
            "px-5",
            isShortViewport ? "h-14" : "h-16"
          )}>
              <button
                type="button"
                data-slot="app-sidebar-brand"
                // The brand has one stable meaning at every rail width: Home.
                onClick={() => {
                  onNavigate('dashboard');
                  setOpenMobile(false);
                }}
                aria-label="Go to Home"
                className={cn(
                  'flex cursor-pointer items-center gap-[12px] border-0 bg-transparent p-0 outline-none',
                  'w-[28px] overflow-hidden gap-0',
                )}
              >
                <div
                  data-slot="app-sidebar-brand-logo"
                  className="overflow-hidden flex-shrink-0 w-[28px] h-[28px] transition-all [transition-duration:var(--duration-slide)] [transition-timing-function:var(--ease-spring)]"
                >
                  {logoSrc && (
                    <img
                      src={logoSrc}
                      alt="Quantis"
                      className="w-full h-full object-contain"
                      // Light mode: render the source artwork as-is so the
                      // brand purple shows through against the neutral
                      // sidebar material. Dark mode: flatten to white via
                      // `brightness(0) invert(1)` so the logo stays legible
                      // on the dark material.
                      style={{
                        filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                      }}
                      onError={(e) => {
                        console.error('[AppSidebar] Failed to load logo:', logoSrc);
                        console.error('[AppSidebar] Logo loading state:', { logosLoading, logoFullLight, logoFullDark, logoIconLight, logoIconDark });
                        // Prevent infinite loop if fallback also fails
                        if (!(e.target as HTMLImageElement).dataset.fallbackAttempted) {
                          (e.target as HTMLImageElement).dataset.fallbackAttempted = 'true';
                        }
                      }}
                    />
                  )}
                  {(!logoSrc && !logosLoading) && (
                    <div className="w-full h-full flex items-center justify-center font-apple text-sidebar-foreground" style={{ fontSize: 'var(--text-sm)' }}>
                      Q
                    </div>
                  )}
                </div>
                <div data-slot="app-sidebar-brand-label" className="min-w-0">
                    <div
                      className="font-apple text-sidebar-foreground leading-none tracking-[-0.02em]"
                      style={{ fontSize: isShortViewport ? '1rem' : '1.1rem', fontWeight: 600 }}
                    >
                      Quantis
                    </div>
                </div>
              </button>
          </header>
        </SidebarHeader>
          <MainRailEmptyContextMenu actions={mainRailMenuActions}>
            <SidebarContent className="min-h-0 overflow-y-auto px-3 pt-2 bg-transparent">
              <SidebarGroup className="p-0"><SidebarGroupContent><SidebarMenu className={navMenuGap}>
                {renderRailWithGaps(mainRailItems, (item) => renderSidebarButton(item, () => handleItemClick(item), true, false, railShortcutFor(item.id)))}
                {renderTabletRailAlerts()}
                {renderRecentGroup()}
              </SidebarMenu></SidebarGroupContent></SidebarGroup>
            </SidebarContent>
          </MainRailEmptyContextMenu>
          <SidebarFooter className="p-0 border-none bg-transparent shrink-0">{tabletRailSupport}{profileSection}</SidebarFooter>
        </Sidebar>
      )}
      <Sidebar
        ref={sidebarElementRef}
        data-labelled-main-rail={standaloneMainRail || undefined}
        data-main-rail-label-mode={standaloneMainRail && mainRailIconsOnly ? 'icons-only' : undefined}
        data-rail-lists-messages={standaloneMainRail && mainRailItems.some((item) => item.id === 'messages') || undefined}
        data-labelled-section-rail={usesSectionRailLabels || undefined}
        data-section-rail-collapsed={sectionRailCollapsed || undefined}
        data-second-rail-hidden={secondRailHidden || undefined}
        data-context-rail={showContextAppRail || undefined}
        data-desktop-toolbar-brand={desktopToolbarBrand || undefined}
        aria-label={hasContextNavigation ? "Section navigation" : "Main navigation"}
        // Desktop keeps `none` — the floating card is a plain in-flow div and
        // the styling below depends on it. Mobile needs the offcanvas drawer
        // instead: `none` early-returns in Sidebar BEFORE its isMobile branch,
        // so with `none` the drawer was never constructed and the sidebar was
        // simply `hidden` (see the lg gate below). That left a phone with no
        // way at all to reach the project's sections once the bottom nav is
        // suppressed, which is exactly what the native iOS shell does.
        collapsible={isMobile ? 'offcanvas' : 'none'}
        variant="sidebar"
        data-collapsed={standaloneMainRail || usesSectionRailLabels || isSidebarCollapsed && !messagesSectionsOpen}
        data-project-navigation-only={restrictToProjectSections || undefined}
        data-section-navigation-only={isMobile && hasContextNavigation || undefined}
        onClick={(event) => {
          if (isMobile || usesTabletSidebarLayout || standaloneMainRail || usesSectionRailLabels || restrictToProjectSections || documentsNavigationOpen || ramsDesktopOutlineOpen) return;
          const target = event.target;
          if (!(target instanceof Element)) return;
          // Blank rail space is an affordance too; navigation, account and
          // window-drag controls keep their own actions.
          if (target.closest('button, a, input, select, textarea, [role="button"], [data-quantis-window-drag]')) return;
          setDesktopSidebarCollapsed(!isSidebarCollapsed);
        }}
        // Floating sidebar treatment per Apple HIG (developer.apple.com
        // /design/human-interface-guidelines/sidebars):
        //   • inset margin from viewport edges (m-2 = 8 px)
        //   • rounded outer corners (Apple uses ~14–16 px on sidebars)
        //   • height calc'd to leave room for the margin so the
        //     SidebarProvider's flex doesn't overflow
        //   • drop shadow for elevation
        //   • backdrop-blur + translucent `--sidebar` so the canvas
        //     bleeds through (canvas is on `qs-shell-root` underneath)
        // collapsible="none" stays — the floating look is purely visual.
        // Width animates between expanded (`--sidebar-width`, 16 rem)
        // and collapsed-to-icons (64 px). The CSS `qs-sidebar-collapsed`
        // class on body globals.css drives label visibility.
        className={cn(
          // `relative` is load-bearing: collapsible="none" renders a plain
          // in-flow div, so the z-40 that sat here for a long time did nothing
          // at all (z-index is ignored on statically positioned elements). It
          // went unnoticed while the scroll-edge blur stopped at the content
          // column. That band is now full-window, so without a real stacking
          // position the frost paints straight over the sidebar's logo row.
          // z-50 clears the band's z-49; Radix overlays portal to body and
          // compare outside this subtree, so they still land on top.
          "group/rail relative z-50 !shrink-0 overflow-visible",
          // macOS inset / floating sidebar: a translucent rounded card inset
          // from the window edges (mt-3 = 12px top gap, mx-2/mb-2 = 8px sides +
          // bottom), rounded 16px outer corners, soft drop shadow for
          // elevation, with the canvas bleeding through its vibrancy. The
          // float (gap + shadow) is the separator — no hairline border needed.
          // max-h leaves room for the margins so the flex row doesn't overflow.
          "mt-3 mx-2 mb-2 rounded-[16px] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.18),0_8px_24px_-8px_rgba(0,0,0,0.10)] !h-auto max-h-[calc(100svh-20px)] backdrop-blur-xl backdrop-saturate-150",
          usesTabletSidebarLayout
            ? isSidebarCollapsed
              ? "pointer-events-none"
              : ""
            : "",
          // In-flow from 1024px on every device, the same cut the Sidebar
          // primitive uses for its drawer; below it this element is not
          // rendered at all. `lg` also hides it on the first frame, before
          // the primitive knows the viewport is narrow.
          "hidden lg:flex"
        )}
        style={{
          // `bg-sidebar` is set by shadcn directly on this element, but
          // we need the colour to remain the source of truth so that
          // theme switches still work — explicitly pinning it here keeps
          // the translucent `--sidebar` value applied even though we
          // also stack the backdrop-blur on top.
          background: showContextAppRail ? 'color-mix(in srgb, var(--sidebar-solid) 86%, var(--sidebar-foreground) 14%)' : 'var(--sidebar)',
          width: standaloneMainRail ? 'var(--qs-primary-rail-width, 70px)' : documentsNavigationOpen ? documentPreviewOpen ? '64px' : '20vw' : usesTabletSidebarLayout
            ? isSidebarCollapsed ? '0px' : 'clamp(168px, 19vw, 208px)'
            : messagesSectionsOpen ? '280px' : usesSectionRailLabels ? 'var(--qs-primary-rail-width, 70px)' : isSidebarCollapsed ? '64px' : ramsDesktopOutlineOpen ? '240px' : showContextAppRail ? 'clamp(184px, 16vw, 220px)' : 'var(--sidebar-width)',
          minWidth: documentsNavigationOpen ? documentPreviewOpen ? 64 : 240 : undefined,
          maxWidth: documentsNavigationOpen ? documentPreviewOpen ? 64 : 300 : undefined,
          marginLeft: usesTabletSidebarLayout && isSidebarCollapsed ? 0 : undefined,
          marginRight: usesTabletSidebarLayout && isSidebarCollapsed ? 0 : undefined,
          opacity: usesTabletSidebarLayout && isSidebarCollapsed ? 0 : 1,
          WebkitBackdropFilter: 'blur(30px) saturate(180%)',
          backdropFilter: 'blur(30px) saturate(180%)',
        }}
      >
        {!showContextAppRail && !restrictToProjectSections && !(isMobile && hasContextNavigation) && (
        <SidebarHeader className="p-0 shrink-0 border-none bg-transparent relative z-20">

          <header
            data-slot="app-sidebar-top-row"
            data-quantis-window-drag="true"
            onMouseDown={handleSidebarHeaderDrag}
            className={cn(
            "flex items-center justify-between relative bg-inherit",
            "px-5",
            isShortViewport ? "h-14" : "h-16"
          )}>
              <button
                type="button"
                data-slot="app-sidebar-brand"
                // The brand has one stable meaning at every rail width: Home.
                onClick={() => {
                  onNavigate('dashboard');
                  setOpenMobile(false);
                }}
                aria-label="Go to Home"
                className={cn(
                  'flex cursor-pointer items-center gap-[12px] border-0 bg-transparent p-0 outline-none',
                  // Compact/native rails keep the Home hit area to the logo.
                  // Desktop browser chrome reserves space for the full wordmark.
                  isSidebarCollapsed && 'w-[28px] overflow-hidden gap-0',
                  usesTabletSidebarLayout && 'min-h-11',
                )}
              >
                <div
                  data-slot="app-sidebar-brand-logo"
                  className="overflow-hidden flex-shrink-0 w-[28px] h-[28px] transition-all [transition-duration:var(--duration-slide)] [transition-timing-function:var(--ease-spring)]"
                >
                  {logoSrc && (
                    <img
                      src={logoSrc}
                      alt="Quantis"
                      className="w-full h-full object-contain"
                      // Light mode: render the source artwork as-is so the
                      // brand purple shows through against the neutral
                      // sidebar material. Dark mode: flatten to white via
                      // `brightness(0) invert(1)` so the logo stays legible
                      // on the dark material.
                      style={{
                        filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                      }}
                      onError={(e) => {
                        console.error('[AppSidebar] Failed to load logo:', logoSrc);
                        console.error('[AppSidebar] Logo loading state:', { logosLoading, logoFullLight, logoFullDark, logoIconLight, logoIconDark });
                        // Prevent infinite loop if fallback also fails
                        if (!(e.target as HTMLImageElement).dataset.fallbackAttempted) {
                          (e.target as HTMLImageElement).dataset.fallbackAttempted = 'true';
                        }
                      }}
                    />
                  )}
                  {(!logoSrc && !logosLoading) && (
                    <div className="w-full h-full flex items-center justify-center font-apple text-sidebar-foreground" style={{ fontSize: 'var(--text-sm)' }}>
                      Q
                    </div>
                  )}
                </div>
                <div data-slot="app-sidebar-brand-label" className="min-w-0">
                    <div
                      className="font-apple text-sidebar-foreground leading-none tracking-[-0.02em]"
                      style={{ fontSize: isShortViewport ? '1rem' : '1.1rem', fontWeight: 600 }}
                    >
                      Quantis
                    </div>
                </div>
              </button>
          </header>
        </SidebarHeader>
        )}


        <MainRailEmptyMenuIf enabled={standaloneMainRail} actions={mainRailMenuActions}>
        <SidebarContent className={cn(
          "relative z-0 min-h-0 overflow-hidden px-3 pt-2 group-data-[collapsible=icon]:px-1.5 bg-transparent",
          usesTabletSidebarLayout && "px-2.5",
          restrictToProjectSections && "h-full pb-2 !overflow-hidden",
          // All 16 project sections have to fit the screen, so that list runs
          // at the tight gap regardless of viewport height.
          isShortViewport || restrictToProjectSections ? "gap-0.5" : "gap-1"
        )}>
          {/*
            Every module can supply a variable number of navigation rows
            (Pricing bills are the obvious example). Keep those rows in one
            bounded native scroll region instead of allowing an individual
            section to shrink and clip its children behind the account row.

            The account controls remain a sibling below this region on short
            tablet viewports, or in SidebarFooter on taller layouts, so they
            never cover the final navigation item. This is deliberately a
            shell-level rule rather than a Pricing exception: new modules and
            longer workspace-driven lists inherit the same safe behaviour.
          */}
          <div
            ref={sidebarScrollRegionRef}
            data-slot="app-sidebar-scroll-region"
            className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]"
          >
          {documentsNavigationOpen && <div id={DOCUMENT_FOLDER_NAVIGATION_PORTAL_ID} data-slot="app-sidebar-project-files" className="min-h-0 flex-1" />}
          {messagesSectionsOpen && <div id="messages-conversations-rail-host" data-slot="app-sidebar-messages-conversations" className="h-full min-h-0 flex-1" />}

          {/* Drawing tools take the rail whenever a sheet is open. Rendered
              first and every other section block yields to it, so this is the
              one mode that wins outright — you cannot be navigating sections
              and drawing at the same time. */}
          {takeoffToolsOpen && takeoffTools && (
            <section data-slot="app-sidebar-takeoff-tools" className="overflow-hidden">
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    <SidebarMenuItem>
                      <TakeoffSidebarToolRail
                        ctx={takeoffTools.ctx}
                        activeTool={takeoffTools.activeTool}
                        collapsed={isSidebarCollapsed}
                        metrics={takeoffRailMetrics}
                        tooltipFor={collapsedSidebarTooltip}
                      />
                    </SidebarMenuItem>

                    <div className="my-1 mx-2 h-px bg-sidebar-border" aria-hidden />
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        data-slot="takeoff-rail-types"
                        isActive={takeoffTypesOpen}
                        aria-pressed={takeoffTypesOpen}
                        aria-label={takeoffTypesOpen ? 'Hide Types inspector' : 'Show Types inspector'}
                        onClick={() => window.dispatchEvent(new CustomEvent('takeoff:open-types-inspector'))}
                        tooltip={collapsedSidebarTooltip('Takeoff Types')}
                        className={cn(NAV_ITEM_ROW_COLLAPSIBLE, rowMetrics.topRow)}
                      >
                        <div className={cn('relative w-[28px] shrink-0 flex items-center justify-center', rowMetrics.iconWrapH)}>
                          <AdskGlyph name="layers" fallback={SidebarRightIcon} className={`${rowMetrics.topIcon} shrink-0 ${takeoffTypesOpen ? 'text-nav-item-active-text' : 'text-nav-item-text'}`} />
                        </div>
                        <span className={`font-apple ${rowMetrics.label} font-medium group-data-[collapsible=icon]:hidden`}>
                          Types
                        </span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                    {/* Scale presets. Only the alternative viewer sends these:
                        it has no scale picker of its own, whereas the built-in
                        canvas already offers one in the header. Dispatches the
                        same takeoff:set-scale event that picker does, so there
                        is one scale path rather than two that could disagree. */}
                    {/* Scale presets. Rendered as a real SidebarMenuButton so
                        the row is geometrically identical to the tools above in
                        both states — hand-rolling the box left the ruler 6px
                        right of every other glyph once the rail collapsed. The
                        dropdown is an invisible overlay across the row, so the
                        button stays the visible target and still opens the list
                        when only icons are showing.

                        Only the alternative viewer sends these: the built-in
                        canvas already offers scale in the header, and two
                        controls for one value would drift apart. */}
                    {takeoffTools.scale && (() => {
                      const denom = takeoffTools.scale.denom;
                      const label = denom != null ? `1 : ${denom}` : 'Set scale';
                      const tooltip = collapsedSidebarTooltip(denom != null ? `Scale ${label}` : 'Set drawing scale');
                      const pill = (
                        <DropdownMenuTrigger asChild>
                          <SidebarMenuButton
                            aria-label="Set drawing scale"
                            className={cn(NAV_ITEM_ROW_COLLAPSIBLE, rowMetrics.topRow)}
                          >
                            <div className={cn('relative w-[28px] shrink-0 flex items-center justify-center', rowMetrics.iconWrapH)}>
                              <Ruler className={`${rowMetrics.topIcon} shrink-0 text-nav-item-text`} />
                            </div>
                            <span className={`font-apple ${rowMetrics.label} font-medium group-data-[collapsible=icon]:hidden`}>
                              {label}
                            </span>
                          </SidebarMenuButton>
                        </DropdownMenuTrigger>
                      );
                      return (
                        <SidebarMenuItem>
                          {/* The shared dropdown rather than a browser select:
                              the browser puts a select's list where it likes,
                              this opens beside the pill like the other rail
                              panels, and it can carry Custom (calibrate) as
                              the canvas header's scale menu does. */}
                          <DropdownMenu>
                            {tooltip ? (
                              <Tooltip>
                                <TooltipTrigger asChild>{pill}</TooltipTrigger>
                                <TooltipContent side="right" align="center" {...tooltip} />
                              </Tooltip>
                            ) : pill}
                            <DropdownMenuContent side="right" align="start" className="min-w-[160px]">
                              <DropdownMenuRadioGroup
                                value={denom != null ? String(denom) : ''}
                                onValueChange={value => {
                                  const picked = Number(value);
                                  if (!Number.isFinite(picked) || picked <= 0) return;
                                  window.dispatchEvent(new CustomEvent('takeoff:set-scale', {
                                    detail: { denom: picked, label: `1 : ${picked}` },
                                  }));
                                }}
                              >
                                {/* A scale measured off the drawing (1 : 48, say)
                                    is rarely a preset. Without its own row the
                                    menu would tick nothing over a calibrated
                                    sheet. */}
                                {denom != null && !SCALE_PRESETS.some(preset => preset.n === denom) && (
                                  <DropdownMenuRadioItem value={String(denom)}>{label}</DropdownMenuRadioItem>
                                )}
                                {SCALE_PRESETS.map(preset => (
                                  <DropdownMenuRadioItem key={preset.n} value={String(preset.n)}>{preset.label}</DropdownMenuRadioItem>
                                ))}
                              </DropdownMenuRadioGroup>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onSelect={() => window.dispatchEvent(new CustomEvent('takeoff:calibrate'))}>
                                Custom, measure a known distance
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </SidebarMenuItem>
                      );
                    })()}

                    {/* Delete selection. Only the alternative viewer sends
                        these: its own toolbar is hidden in favour of this
                        rail, and its delete button went with it, leaving
                        deletion keyboard-only otherwise. Gated on the same
                        takeoffTools.scale check as the row above rather than
                        a second flag for the same "alternative viewer"
                        condition. */}
                    {takeoffTools.scale && (() => {
                      const tooltip = collapsedSidebarTooltip('Delete');
                      const pill = (
                        <DropdownMenuTrigger asChild>
                          <SidebarMenuButton aria-label="Delete" className={cn(NAV_ITEM_ROW_COLLAPSIBLE, rowMetrics.topRow)}>
                            <div className={cn('relative w-[28px] shrink-0 flex items-center justify-center', rowMetrics.iconWrapH)}>
                              <Trash2 className={`${rowMetrics.topIcon} shrink-0 text-nav-item-text`} />
                            </div>
                            <span className={`font-apple ${rowMetrics.label} font-medium group-data-[collapsible=icon]:hidden`}>
                              Delete
                            </span>
                          </SidebarMenuButton>
                        </DropdownMenuTrigger>
                      );
                      return (
                        <SidebarMenuItem>
                          {/* The confirmation sits beside the pill like the
                              other rail panels, rather than in a centred
                              dialog. The item is the confirmation, so the
                              handler deletes without asking again. */}
                          <DropdownMenu>
                            {tooltip ? (
                              <Tooltip>
                                <TooltipTrigger asChild>{pill}</TooltipTrigger>
                                <TooltipContent side="right" align="center" {...tooltip} />
                              </Tooltip>
                            ) : pill}
                            <DropdownMenuContent side="right" align="start" className="min-w-[200px]">
                              <DropdownMenuItem
                                variant="destructive"
                                onSelect={() => window.dispatchEvent(new CustomEvent('takeoff:delete-selected'))}
                              >
                                Delete selected
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </SidebarMenuItem>
                      );
                    })()}

                    {/* Snap to the drawing. With it off every pick lands on the
                        pixel under the cursor, which is freehand and cannot
                        trace a wall. It stays a toggle rather than simply being
                        on because snapping discards a pick that finds no
                        geometry, so blank paper needs it off. */}
                    {takeoffTools.snap !== null && (() => {
                      const snapOn = takeoffTools.snap;
                      const tooltip = collapsedSidebarTooltip(snapOn ? 'Snap to drawing: on' : 'Snap to drawing: off');
                      const pill = (
                        <DropdownMenuTrigger asChild>
                          <SidebarMenuButton
                            aria-label="Snap to drawing"
                            isActive={snapOn}
                            className={cn(NAV_ITEM_ROW_COLLAPSIBLE, rowMetrics.topRow)}
                          >
                            <div className={cn('relative w-[28px] shrink-0 flex items-center justify-center', rowMetrics.iconWrapH)}>
                              {/* Two icons rather than one styled by isActive:
                                  collapsed, the label is hidden and the active
                                  tint alone does not say which way it is set. */}
                              {snapOn
                                ? <Crosshair className={`${rowMetrics.topIcon} shrink-0 text-nav-item-text`} />
                                : <Circle className={`${rowMetrics.topIcon} shrink-0 text-nav-item-text`} />}
                            </div>
                            <span className={`font-apple ${rowMetrics.label} font-medium group-data-[collapsible=icon]:hidden`}>
                              {snapOn ? 'Snap on' : 'Snap off'}
                            </span>
                          </SidebarMenuButton>
                        </DropdownMenuTrigger>
                      );
                      return (
                        <SidebarMenuItem>
                          <DropdownMenu>
                            {tooltip ? (
                              <Tooltip>
                                <TooltipTrigger asChild>{pill}</TooltipTrigger>
                                <TooltipContent side="right" align="center" {...tooltip} />
                              </Tooltip>
                            ) : pill}
                            <DropdownMenuContent side="right" align="start" className="min-w-[180px]">
                              <DropdownMenuLabel>Snap to drawing</DropdownMenuLabel>
                              <DropdownMenuRadioGroup
                                value={snapOn ? 'on' : 'off'}
                                onValueChange={value => {
                                  // The app owns the state and only knows how
                                  // to flip it; a repeat pick must not flip it
                                  // back.
                                  if ((value === 'on') !== snapOn) window.dispatchEvent(new CustomEvent('takeoff:toggle-snap'));
                                }}
                              >
                                <DropdownMenuRadioItem value="on">On</DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="off">Off</DropdownMenuRadioItem>
                              </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </SidebarMenuItem>
                      );
                    })()}

                    <div className="my-1 mx-2 h-px bg-sidebar-border" aria-hidden />
                    {VIEW_RAIL_ENTRIES.filter(entry => (entry.visible ? entry.visible(takeoffTools.ctx) : true)).map((entry) => {
                      // Settings opens a panel the viewer owns, so it has no
                      // pressed state to mirror.
                      const active = entry.id === 'view3d'
                        ? takeoffTools.viewActive.view3d
                        : entry.id === 'summary' ? takeoffTools.viewActive.summary
                        : false;
                      const disabled = entry.enabled ? !entry.enabled(takeoffTools.ctx) : false;
                      return (
                        <SidebarMenuItem key={entry.id}>
                          <SidebarMenuButton
                            isActive={active}
                            aria-pressed={active}
                            aria-label={entry.label(active)}
                            disabled={disabled}
                            onClick={(e) => window.dispatchEvent(
                              new CustomEvent('takeoff:view-rail', { detail: { id: entry.id, anchor: anchorRect(e.currentTarget) } }))}
                            tooltip={collapsedSidebarTooltip(entry.label(active))}
                            className={cn(NAV_ITEM_ROW_COLLAPSIBLE, rowMetrics.topRow, disabled && 'opacity-40')}
                          >
                            <div className={cn("relative w-[28px] shrink-0 flex items-center justify-center", rowMetrics.iconWrapH)}>
                              <ToolIcon entry={entry} ctx={takeoffTools.ctx} className={`${rowMetrics.topIcon} shrink-0 ${active ? 'text-nav-item-active-text' : 'text-nav-item-text'}`} />
                            </div>
                            <span className={`font-apple ${rowMetrics.label} font-medium ${active ? 'text-nav-item-active-text font-semibold' : ''} group-data-[collapsible=icon]:hidden`}>
                              {entry.label(active)}
                            </span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                  {/* Host for the canvas's scale/zoom cluster, which portals in
                      here (see railHosted in TakeoffPreviewCanvas). Empty in the
                      DOM until it does — the canvas owns that content. */}
                  <div data-slot="app-sidebar-takeoff-extras" className="mt-1" />
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {/* The way back INTO the tools while the section list is showing. */}
          {takeoffTools && showSectionsOverTools && (
            <SidebarGroup className="p-0">
              <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => setShowSectionsOverTools(false)}
                      tooltip={collapsedSidebarTooltip('Drawing tools')}
                      className={cn(NAV_ITEM_ROW_COLLAPSIBLE, rowMetrics.topRow)}
                    >
                      <div className={cn("relative w-[28px] shrink-0 flex items-center justify-center", rowMetrics.iconWrapH)}>
                        <ChevronRight className={`${rowMetrics.topIcon} shrink-0 text-nav-item-text`} />
                      </div>
                      <span className={`font-apple ${rowMetrics.label} font-medium group-data-[collapsible=icon]:hidden`}>
                        Drawing tools
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {/* Mobile, inside a project: the drawer IS the project's section nav.
              `restrictToProjectSections` negates every app-nav block below, so
              without this positive branch the drawer opened empty — which went
              unnoticed for as long as the sidebar was `hidden` on mobile and
              nothing could open it. On the iPhone shell this is the only route
              to these sections, so an empty drawer is a dead end, not a
              cosmetic bug. Grouped exactly as the sidebar shells group it:
              renderSubnavButton folds `kind: 'heading'` rows into headings and
              closes the drawer on selection. */}
          {!takeoffToolsOpen && restrictToProjectSections && !hsSectionsOpen && (
            <section
              data-slot="app-sidebar-project-sections"
              className="flex min-h-0 flex-1 overflow-hidden [&_[data-slot=sidebar-menu-item]]:h-full [&_[data-slot=sidebar-menu-button]]:h-full"
            >
              <SidebarGroup className="min-h-0 flex-1 p-0">
                <SidebarGroupContent className="min-h-0 flex-1">
                  <SidebarMenu className="grid h-full auto-rows-fr gap-0">
                    {projectNavigationItems.map((tab) => renderSubnavButton('projects', tab))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {hsEditorSectionsOpen && (
            <section
              data-slot="app-sidebar-hs-editor-sections"
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    {HS_EDITOR_SECTION_ITEMS
                      .filter((item) => hsEditorState?.sectionIds.includes(item.id))
                      .map((item) => {
                        const rowId = `${HS_EDITOR_SECTION_PREFIX}${item.id}`;
                        return renderSidebarButton(
                          {
                            id: rowId,
                            label: item.label,
                            icon: HS_EDITOR_SECTION_ICONS[item.id] ?? Info,
                          },
                          () => handleItemClick({ id: rowId }),
                        );
                      })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {hsDocumentSectionsOpen && (
            <section
              data-slot="app-sidebar-hs-document-sections"
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    {HS_DOCUMENT_SECTION_ITEMS
                      .filter((item) => hsDocumentState?.sectionIds.includes(item.id))
                      .map((item) => {
                        const rowId = `${HS_DOCUMENT_SECTION_PREFIX}${item.id}`;
                        return renderSidebarButton(
                          {
                            id: rowId,
                            label: item.label,
                            icon: HS_DOCUMENT_SECTION_ICONS[item.id] ?? Info,
                          },
                          () => handleItemClick({ id: rowId }),
                        );
                      })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {/* Interim application drill-in: an application is open inside the
              project's Applications tab, so the sidebar swaps entirely to the
              application's own section nav instead of the project tab list — mirrors the native
              macOS/iPad sidebar (see buildNativeNavItems' interimApplicationOpen
              branch) and replaces InterimApplication's in-content SECTIONS
              rail, which no longer renders on web. Back row returns to the
              Applications tab via the same sentinel the native chevron uses,
              and is skipped when the header back row is already showing. */}
          {!takeoffToolsOpen && !restrictToProjectSections && !hsSectionsOpen && interimSectionsOpen && (
            <section
              data-slot="app-sidebar-interim-sections"
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    {INTERIM_SECTION_ITEMS
                      .filter((item) => item.id !== 'pay-less' || projectTabProps?.canGivePayLessNotice === true)
                      .map((item) => {
                      const rowId = `${INTERIM_SECTION_PREFIX}${item.id}`;
                      return renderSidebarButton(
                        { id: rowId, label: item.label, icon: INTERIM_SECTION_ICONS[item.id] ?? Info },
                        () => handleItemClick({ id: rowId }),
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {/* Edit-user form open inside a workspace's Users tab: the sidebar
              drills past the workspace section list to the form's own
              sections, and EditUserForm drops its in-content TabsList. Same
              shape as the interim block above, minus its back row — the
              header's leading chevron already returns to the user list, and
              two exits side by side is one too many. */}
          {!takeoffToolsOpen && !restrictToProjectSections && !hsSectionsOpen && !interimSectionsOpen && userFormSectionsOpen && (
            <section
              data-slot="app-sidebar-user-form-sections"
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    {USER_FORM_SECTION_ITEMS.map((item) => {
                      const rowId = `${USER_FORM_SECTION_PREFIX}${item.id}`;
                      return renderSidebarButton(
                        { id: rowId, label: item.label, icon: USER_FORM_SECTION_ICONS[item.id] ?? Info },
                        () => {
                          window.dispatchEvent(new CustomEvent(
                            WORKSPACE_USER_FORM_SET_TAB_EVENT, { detail: { tab: item.id } },
                          ));
                          setOpenMobile(false);
                        },
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {/* Employer add/edit form open on the Employers screen: the sidebar
              becomes the form's section nav, and EmployerForm drops its own
              in-content nav column. Rendered BEFORE the profile block below and
              mutually exclusive with it — the form is one level deeper, and the
              two arrive on independent channels, so a frame where both look open
              has to resolve to the deeper one. Rows dispatch straight to the
              form rather than routing through the app router: the profile's
              sections are in the URL, a transient create form's page is not. */}
          {!takeoffToolsOpen && !restrictToProjectSections && !hsSectionsOpen && !interimSectionsOpen && !userFormSectionsOpen && employerFormSectionsOpen && (
            <section
              data-slot="app-sidebar-employer-form-sections"
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    {employerFormTabItems.map((item) => {
                      const rowId = `${EMPLOYER_FORM_TAB_PREFIX}${item.id}`;
                      return renderSidebarButton(
                        { id: rowId, label: item.label, icon: item.icon ?? Info },
                        () => {
                          window.dispatchEvent(new CustomEvent(
                            EMPLOYER_FORM_SET_SECTION_EVENT, { detail: { sectionId: item.id } },
                          ));
                          setOpenMobile(false);
                        },
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {/* Employer drilled in on the Employers screen: the sidebar becomes
              the profile's section nav, matching what the Workspaces directory
              does when a workspace is selected. No back row: the header's
              chevron is the way out, exactly as the workspace drill-in does it
              (and as buildNativeNavItems already assumes for the shells).
              Each row is its own page: they route through
              EMPLOYER_DETAIL_TAB_PREFIX to the panel, which swaps content. */}
          {isAccountingDestination(currentView) && !restrictToProjectSections && (
            <AccountingSectionNavigation
              currentView={currentView} destinations={accountingDestinations}
              onNavigate={onNavigate} onSelected={() => setOpenMobile(false)}
              rowClassName={cn(NAV_ITEM_ROW_COLLAPSIBLE, rowMetrics.subRow)}
              iconClassName={rowMetrics.subIcon} iconWrapClassName={rowMetrics.iconWrapH}
              menuClassName={navMenuGap} tooltip={usesSectionRailLabels ? sectionRailTooltip : collapsedSidebarTooltip} labelled={usesSectionRailLabels}
            />
          )}

          {/* Sales drill-in supplies the hub's own sections. Clicking routes back
              through `sales:navigate-section`, which SalesView already listens
              for — the sidebar never owns the active section, it only shows it. */}
          {!takeoffToolsOpen && !restrictToProjectSections && !hsSectionsOpen && !interimSectionsOpen && !userFormSectionsOpen && !employerFormSectionsOpen && !employerSectionsOpen && !purchasesSectionsOpen && !financeHubSectionsOpen && salesSectionsOpen && (
            <section
              data-slot="app-sidebar-sales-sections"
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    {salesSections.map((item) => {
                      const Icon = item.Icon as React.ElementType;
                      return (
                        <React.Fragment key={`sales:${item.id}`}>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              isActive={salesActiveSection === item.id}
                              onClick={() => {
                                window.dispatchEvent(new CustomEvent('sales:navigate-section', { detail: { sectionId: item.id } }));
                                setOpenMobile(false);
                              }}
                              data-section-rail-item={usesSectionRailLabels || undefined}
                              tooltip={usesSectionRailLabels ? sectionRailTooltip(item.label) : collapsedSidebarTooltip(item.label)}
                              // Same row treatment as every other sub-nav in this
                              // sidebar. A bare SidebarMenuButton takes the shadcn
                              // default, which has square corners and none of the
                              // nav-item colour tokens — visibly not this app's nav.
                              className={cn(
                                NAV_ITEM_ROW_COLLAPSIBLE,
                                rowMetrics.subRow
                              )}
                            >
                              <div className={cn("relative w-[28px] shrink-0 flex items-center justify-center", rowMetrics.iconWrapH)}>
                                <Icon className={`${rowMetrics.subIcon} shrink-0 transition-colors ${
                                  salesActiveSection === item.id
                                    ? 'text-nav-item-active-text'
                                    : 'text-nav-item-text group-hover:text-nav-item-text-hover'
                                }`} />
                              </div>
                              <span data-slot="section-rail-label" className="truncate">{item.label}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        </React.Fragment>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {/* Purchases drill-in — same shape as Sales above, including the
              mutual exclusion: the two hubs are already exclusive by
              currentView, but salesSectionsOpen/purchasesSectionsOpen key off
              it directly with no clearing effect of their own, so guarding
              each against the other costs nothing and rules out a stale
              double-render on the frame the view switches between them. */}
          {!takeoffToolsOpen && !restrictToProjectSections && !hsSectionsOpen && !interimSectionsOpen && !userFormSectionsOpen && !employerFormSectionsOpen && !employerSectionsOpen && !salesSectionsOpen && !financeHubSectionsOpen && purchasesSectionsOpen && (
            <section
              data-slot="app-sidebar-purchases-sections"
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    {purchasesSections.map((item) => {
                      const Icon = item.Icon as React.ElementType;
                      return (
                        <React.Fragment key={`purchases:${item.id}`}>
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              isActive={purchasesActiveSection === item.id}
                              onClick={() => {
                                window.dispatchEvent(new CustomEvent('purchases:navigate-section', { detail: { sectionId: item.id } }));
                                setOpenMobile(false);
                              }}
                              data-section-rail-item={usesSectionRailLabels || undefined}
                              tooltip={usesSectionRailLabels ? sectionRailTooltip(item.label) : collapsedSidebarTooltip(item.label)}
                              className={cn(
                                NAV_ITEM_ROW_COLLAPSIBLE,
                                rowMetrics.subRow
                              )}
                            >
                              <div className={cn("relative w-[28px] shrink-0 flex items-center justify-center", rowMetrics.iconWrapH)}>
                                <Icon className={`${rowMetrics.subIcon} shrink-0 transition-colors ${
                                  purchasesActiveSection === item.id
                                    ? 'text-nav-item-active-text'
                                    : 'text-nav-item-text group-hover:text-nav-item-text-hover'
                                }`} />
                              </div>
                              <span data-slot="section-rail-label" className="truncate">{item.label}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        </React.Fragment>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}



          {/* Finance-hub drill-in: replaces the old in-content rails. The
              Quantis brand remains the global route Home, so this list only
              contains task-specific destinations. Payroll swaps its rows to
              employee profile pages while a member of staff is open. */}
          {!takeoffToolsOpen && !restrictToProjectSections && !hsSectionsOpen && !interimSectionsOpen && !userFormSectionsOpen && !employerFormSectionsOpen && !employerSectionsOpen && !salesSectionsOpen && !purchasesSectionsOpen && financeHubSectionsOpen && (
            <section
              data-slot={`app-sidebar-${financeHubParent}-sections`}
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    {financeHubItems.map((tab) => renderSubnavButton(financeHubParent, tab))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {!takeoffToolsOpen && !restrictToProjectSections && !hsSectionsOpen && !interimSectionsOpen && !userFormSectionsOpen && !employerFormSectionsOpen && employerSectionsOpen && (
            <section
              data-slot="app-sidebar-employer-sections"
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    {employerDetailTabItems.map((item) => {
                      if (item.kind === 'heading') {
                        return renderSectionHeading(`employer:${item.id}`, item.label);
                      }
                      const rowId = `${EMPLOYER_DETAIL_TAB_PREFIX}${item.id}`;
                      // No optimistic local set: the click routes through to
                      // EmployerManagement, which broadcasts the new section
                      // straight back and drives the highlight above. Setting it
                      // here as well would give the highlight a second source
                      // that could disagree with the page on screen.
                      return renderSidebarButton(
                        { id: rowId, label: item.label, icon: item.icon ?? Info },
                        () => handleItemClick({ id: rowId }),
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {/* Focused mode: when the user is inside a project / system
              settings / client management view, the nav body collapses to
              the section's sub-tabs. Project detail also keeps an explicit
              route back to the projects list for users who miss the compact
              header control. */}
          {!takeoffToolsOpen && !restrictToProjectSections && !hsSectionsOpen && !interimSectionsOpen && !userFormSectionsOpen && !employerFormSectionsOpen && !employerSectionsOpen && !documentsNavigationOpen && focusToActiveSection && activeSubnavParentId && (
            <section
              data-slot="app-sidebar-focused-subnav"
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  {(isProfileView || isSystemSettingsView) ? (
                    <SettingsNavigationGroups expanded={!isMobile} groups={settingsNavigationGroups} onBackActionChange={handleSettingsBackAction}
                      activeId={isSystemSettingsView ? `system-settings:${activeSystemSettingsTabId}` : `profile:${activeProfileSettingsTab}`}
                      onReturnToSettings={() => openSettingsTarget('profile:overview', false)}
                      onOpenGroup={id => openSettingsTarget(id, false)}
                      onSelect={id => {void openSettingsTarget(id, true);}}/>
                  ) : (
                    <SidebarMenu className={navMenuGap}>
                      {getInlineSubnavItems(activeSubnavParentId).map(tab => renderSubnavButton(activeSubnavParentId, tab))}
                    </SidebarMenu>
                  )}
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {calendarSectionsOpen && (
            <SidebarGroup data-slot="app-sidebar-calendar-sections" className="p-0">
              <SidebarGroupContent><SidebarMenu className={navMenuGap}>
                {CALENDAR_VIEW_OPTIONS.map(view => renderSubnavButton('calendar', { id: view.id, label: view.label, icon: CalendarDays }))}
              </SidebarMenu></SidebarGroupContent>
            </SidebarGroup>
          )}
          {currentView === 'pipeline' && isMobile && !NativeBridge.isRunningNatively() && (
            <SidebarGroup data-slot="app-sidebar-enquiry-stages" className="p-0">
              <SidebarGroupContent>
                <SidebarMenu className={navMenuGap}>
                  {CONSULTANCY_STAGES.map((stage) => renderSubnavButton('pipeline', {
                    id: stage.id,
                    label: stage.label,
                    icon: List,
                    badge: pipelineNavigation?.counts[stage.id],
                  }))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
          {!showContextAppRail && !takeoffToolsOpen && !restrictToProjectSections && !hsSectionsOpen && !employerFormSectionsOpen && !employerSectionsOpen && !salesSectionsOpen && !purchasesSectionsOpen && !financeHubSectionsOpen && !calendarSectionsOpen && !focusToActiveSection && (visibleMainNav.length > 0 || visibleCrmNav.length > 0 || visibleAdminNav.length > 0) && (
            <section
              data-slot="app-sidebar-nav"
              className="overflow-hidden group-data-[collapsible=icon]:!max-h-none group-data-[collapsible=icon]:!opacity-100 max-h-[2000px] opacity-100"
            >
              <SidebarGroup className="p-0">
                <SidebarGroupContent>
                  <SidebarMenu className={navMenuGap}>
                    {renderRailWithGaps([...visibleMainNav, ...visibleCrmNav, ...visibleAdminNav], (item) => (
                      renderSidebarButton(item, () => handleItemClick(item), false, true, railShortcutFor(item.id))
                    ))}
                    {standaloneMainRail && renderTabletRailAlerts()}
                    {renderRecentGroup()}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </section>
          )}

          {!restrictToProjectSections && (
            <div className={cn(isShortViewport ? "pb-1" : "pb-4", "shrink-0")} />
          )}

          </div>

          {isShortViewport && !restrictToProjectSections && !showContextAppRail && !(isMobile && hasContextNavigation) ? (
            <>
              <ReleaseUpdateRow />
              {standaloneMainRail && tabletRailSupport}
              <div className="shrink-0">
                {profileSection}
              </div>
            </>
          ) : null}
        </SidebarContent>
        </MainRailEmptyMenuIf>
        
        {!isShortViewport && !restrictToProjectSections && !showContextAppRail && !(isMobile && hasContextNavigation) ? (
          <SidebarFooter className="p-0 border-none bg-transparent shrink-0">
            <ReleaseUpdateRow />
            {standaloneMainRail && tabletRailSupport}
            {profileSection}
          </SidebarFooter>
        ) : null}
      </Sidebar>
      {customiseRailDialog}
    </div>
  );
}

/** "Release update" row, shown in the sidebar footer when a newer build is live.
 *
 *  Replaces the floating pill that used to sit at the bottom-right of the
 *  viewport. That competed with page content for something that is not urgent,
 *  and in the macOS shell it hovered over the canvas. An update is a property of
 *  the app, so it belongs in the app's own chrome next to the account row.
 *
 *  Deliberately a presenter only: UpdateNotificationBanner still owns the
 *  version poll, the dismissed-build key, the safe-boundary auto-reload and the
 *  forced modal. This listens for availability and sends back the intent.
 */
function ReleaseUpdateRow() {
  const [update, setUpdate] = React.useState<AppUpdateStateDetail | null>(null);
  const usesTabletSidebarLayout = useTabletSidebarLayout();
  const isDesktopFrame = useDesktopFrame();
  const isTouchRuntime = useMemo(() => !isDesktopFrame && isTouchShellRuntime({
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    platform: typeof navigator === 'undefined' ? '' : navigator.platform,
    maxTouchPoints: typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
    isDesktopViewport: true,
  }), [isDesktopFrame]);

  React.useEffect(() => {
    const onState = (e: Event) => {
      const detail = (e as CustomEvent<AppUpdateStateDetail>).detail;
      setUpdate(detail?.available ? detail : null);
    };
    window.addEventListener(APP_UPDATE_STATE_EVENT, onState);
    const current = getLatestAppUpdateState();
    setUpdate(current.available ? current : null);
    return () => window.removeEventListener(APP_UPDATE_STATE_EVENT, onState);
  }, []);

  if (!update) return null;

  const versionLabel = update.version ? `v${update.version}` : 'New version';
  const compactBuild = update.build ? update.build.slice(0, 7) : null;
  const buildLabel = compactBuild ? `build ${compactBuild}` : null;
  const releaseMeta = buildLabel ? `${versionLabel} · ${buildLabel}` : versionLabel;
  const fullReleaseMeta = [
    update.version ? `Version ${update.version}` : 'New version',
    update.build ? `Build ${update.build}` : null,
  ].filter(Boolean).join(' · ');
  const builtAtDate = update.builtAt ? new Date(update.builtAt) : null;
  const builtDate = builtAtDate && Number.isFinite(builtAtDate.getTime())
    ? new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(builtAtDate)
    : null;

  return (
    <div className="px-2 pb-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent(APP_UPDATE_APPLY_EVENT))}
            className={cn(
              'group flex w-full items-center gap-[12px] rounded-[6px] px-2 py-1.5',
              'text-[13px] font-medium text-nav-item-text',
              'hover:bg-nav-item-hover-bg hover:text-nav-item-text-hover',
              'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0',
            )}
          >
            <div className="relative w-[28px] shrink-0 flex items-center justify-center">
              <RefreshCw className="h-4 w-4 shrink-0" />
              {/* The only signal that anything is waiting once the pill is gone, so
                  it has to survive the collapsed rail where the label does not. */}
              <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
            </div>
            <span className="min-w-0 flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
              <span className="block truncate">Release update</span>
              <span className="mt-0.5 block truncate text-[10px] font-normal text-nav-item-text">
                {releaseMeta}
              </span>
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" align="center" hidden={isTouchRuntime || usesTabletSidebarLayout}>
          {`${fullReleaseMeta}${builtDate ? `, published ${builtDate}` : ''}. Reload to update.`}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
